<template>
  <div v-if="manifest">
    <validation-observer ref="managementManifestValidator">
      <v-card flat>
        <!-- マニフェストの種類（１次or２次） -->
        <v-card-subtitle>
          <v-chip class="ma-2" color="info accent-3" text-color="white">
            {{ this.level === "first_manifest" ? "１" : "２" }}次マニフェスト
          </v-chip>
        </v-card-subtitle>
        <!-- /マニフェストの種類（１次or２次） -->

        <!-- マニフェスト種別 -->
        <v-card-text class="pt-0">
          <v-row v-if="!isEdit && !$route.params.duplicate_id">
            <v-col cols="12" md="6">
              <v-btn
                :class="manifest.form_type === 'master_mode' ? 'primary' : ''"
                block
                large
                elevation="2"
                @click="formTypeButtonClickEvent('master_mode')"
              >
                マスタから登録
              </v-btn>
            </v-col>
            <v-col cols="12" md="6">
              <v-btn
                :class="manifest.form_type === 'free_mode' ? 'primary' : ''"
                block
                large
                elevation="2"
                @click="formTypeButtonClickEvent('free_mode')"
              >
                フリー登録
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /マニフェスト種別 -->

        <!-- DXE起票担当者 -->
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="DXE起票担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="manifest.dxe_vote_staff"
                  :error-messages="errors"
                  :items="voteStaffs"
                  :return-object="false"
                  dense
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>DXE起票担当者</span>
                    <span class="ml-1">
                      ({{ manifest.dxe_vote_staff?.length || 0 }} / 12)
                    </span>
                  </template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /DXE起票担当者 -->

        <!-- 排出事業者を選択 -->
        <template
          v-if="manifest.form_type === 'master_mode'"
        >
          <v-card-title class="pt-0">
            <span style="color: #0074ba">排出事業者{{ $route.params.id ? '' : 'を選択' }}</span>
          </v-card-title>
          <template
            v-if="!isEdit"
          >
            <!-- 1次マニフェスト -->
            <v-card-text v-if="this.level === 'first_manifest'" class="pt-0">
              <infinite-emission-and-location-auto-complete
                ref="emission"
                :supplier-id="manifest.supplierable_id"
                :location-id="manifest.supplierable_location_id"
                :location-code="manifest.supplierable_location_code"
                :supplier-required="true"
                :without-invisible="true"
                :without-general-form="true"
                :location-required="!manifest.use_spot_emission_location"
                :location-disabled="manifest.use_spot_emission_location"
                location-auto-select
                supplier-md="4"
                location-md="4"
                @update:values="supplierChangeCallback"
              >
                <template v-slot:supplier-selection="{ item }">
                  <span>{{ item.code }}:{{ item.text }}</span>
                </template>
                <template v-slot:append>
                  <v-col cols="12" md="4">
                    <div class="d-flex">
                      <div>
                        <v-btn
                          class="primary"
                          height="40"
                          @click="searchEmissionButtonClickEvent"
                        >
                          排出事業者を検索
                        </v-btn>
                      </div>
                      <div
                        v-if="level === 'first_manifest' && selectedEmission"
                        class="ml-3"
                      >
                        <div class="float-left mt-2">
                          <v-checkbox
                            v-model="manifest.use_spot_emission_location"
                            dense
                            hide-details="auto"
                            class="mt-0"
                            small
                            @change="spotEmissionLocationChangeEvent"
                          >
                            <template v-slot:label>
                              <small>スポット排出事業場&emsp;</small>
                            </template>
                          </v-checkbox>
                        </div>
                        <div class="float-left mt-2">
                          <v-btn
                            v-if="manifest.use_spot_emission_location"
                            small
                            @click="editSpotLocationButtonClickEvent"
                          >
                            <v-icon small>mdi-pencil-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </template>
              </infinite-emission-and-location-auto-complete>
            </v-card-text>
            <!-- /1次マニフェスト -->

            <!-- 2次マニフェスト -->
            <v-card-text v-else>
              <infinite-tenant-trader-and-location-auto-complete
                ref="emission"
                :trader-id="manifest.supplierable_id"
                :location-id="manifest.supplierable_location_id"
                :location-code="manifest.supplierable_location_code"
                :trader-required="true"
                :location-required="true"
                search-type="trader"
                trader-md="4"
                location-md="4"
                location-auto-select
                @update:values="supplierChangeCallback"
              >
                <template v-slot:append>
                  <v-col cols="12" md="3">
                    <div class="d-flex">
                      <div>
                        <v-btn class="primary" @click="searchEmissionButtonClickEvent">
                          排出事業者を検索
                        </v-btn>
                      </div>
                      <div
                        v-if="level === 'first_manifest' && selectedEmission"
                        class="ml-3"
                      >
                        <div class="float-left">
                          <v-checkbox
                            v-model="manifest.use_spot_emission_location"
                            :disabled="manifest.lock_spot_emission_location"
                            dense
                            hide-details="auto"
                            class="mt-0"
                            small
                            @change="spotEmissionLocationChangeEvent"
                          >
                            <template v-slot:label>
                              スポット排出事業場&emsp;
                            </template>
                          </v-checkbox>
                        </div>
                        <div class="float-left">
                          <v-btn
                            v-if="manifest.use_spot_emission_location"
                            small
                            @click="editSpotLocationButtonClickEvent"
                          >
                            <v-icon small>mdi-pencil-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </template>
              </infinite-tenant-trader-and-location-auto-complete>
            </v-card-text>
            <!-- /2次マニフェスト -->
          </template>
          <!-- /排出事業者検索 -->

          <!-- 排出事業者・事業場詳細 -->
          <v-card-text v-if="manifest.supplierable_id" class="pt-0">
            <v-row>
              <v-col cols="12" md="4">
                <h3>{{ selectedEmission.name }}</h3>
                <ul style="list-style: none" class="text-small">
                  <li>
                    〒{{ selectedEmission.zip_code }}&emsp;{{
                      selectedEmission.address
                    }}
                  </li>
                  <li>電話番号: {{ selectedEmission.phone_number || "-" }}</li>
                  <li>メモ:&nbsp;{{ selectedEmission.memo || "-" }}</li>
                  <li>
                    <strong v-if="this.level === 'first_manifest'"
                    >マスタ区分:
                      {{
                        selectedEmission.manifest_type | manifest_type_to_label
                      }}</strong
                    >
                  </li>
                </ul>
              </v-col>
              <v-col
                v-if="selectedEmission.location && selectedEmission.location.name"
                cols="12"
                md="4"
              >
                <h3>{{ selectedEmission.location.name }}</h3>
                <ul style="list-style: none" class="text-small">
                  <li>
                    〒{{ selectedEmission.location.zip_code }}&emsp;{{
                      selectedEmission.location.address
                    }}
                  </li>
                  <li>
                    電話番号:
                    {{ selectedEmission.location.phone_number || "-" }}
                  </li>
                  <li>
                    メモ:&nbsp;{{ selectedEmission.location.memo || "-" }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-card-text>
          <!-- 排出事業者・事業場詳細 -->

          <!-- 回収履歴 -->
          <v-row
            v-if="!isEdit"
            dense
            style="padding-left: 24px"
          >
            <v-col cols="12" md="6">
              <manifest-history
                :type-of-manifest="level"
                :supplierable-id.sync="manifest.supplierable_id"
                :supplierable-location-code.sync="manifest.supplierable_location_code"
                @reflect:manifest="reflectHistoryCallback"
              />
            </v-col>
          </v-row>
          <!-- /回収履歴 -->

        </template>
        <!-- /排出事業者を選択 -->

        <!-- 排出事業者を登録 -->
        <template v-if="manifest.form_type === 'free_mode'">
          <v-card-title class="pt-0">
            <span style="color: #0074ba">排出事業者を登録</span>
          </v-card-title>

          <v-card-text class="pt-0">
            <!-- 会社名 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :class="
                    manifest.form_type === 'free_mode' ? 'requiredMark' : ''
                  "
                  :rules="{
                    required: manifest.form_type === 'free_mode',
                    max: 60,
                  }"
                  :vid="`free_emission_name`"
                  name="会社名"
                >
                  <v-text-field
                    v-model="manifest.emission_name"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）○○○○○株式会社"
                  >
                    <template v-slot:label>
                      <span>会社名</span>
                      <span class="ml-1">
                        ({{ manifest.emission_name?.length || 0 }} / 60)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /会社名 -->
            <!-- 郵便番号 -->
            <v-row>
              <v-col cols="12" md="5">
                <validation-provider
                  v-slot="{ errors }"
                  :class="
                    manifest.form_type === 'free_mode' ? 'requiredMark' : ''
                  "
                  :rules="{
                    required: manifest.form_type === 'free_mode',
                    zip_code: true,
                    max: 8,
                  }"
                  :vid="`free_emission_zip_code`"
                  name="郵便番号"
                >
                  <v-text-field
                    v-model="manifest.emission_zip_code"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    hint="ハイフンを含めて入力してください"
                    outlined
                    persistent-placeholder
                    placeholder="例）111-1111"
                  >
                    <template v-slot:label>
                      <span>郵便番号</span>
                      <span class="ml-1">
                        ({{ manifest.emission_zip_code?.length || 0 }} / 8)
                      </span>
                    </template>
                    <template v-slot:append-outer>
                      <zip-code-search
                        :zip-code="manifest.emission_zip_code"
                        @zipAddress="setEmissionZipAddress"
                      />
                      &nbsp;
                      <v-btn
                        outlined
                        small
                        @click="searchFreeEmissionZipButtonClickEvent"
                      >
                        住所→郵便番号
                      </v-btn>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /郵便番号 -->
            <!-- 都道府県／市区町村 -->
            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_prefecture_code`"
                  class="requiredMark"
                  name="都道府県"
                  rules="required"
                >
                  <v-autocomplete
                    v-model="manifest.emission_prefecture_code"
                    :error-messages="errors"
                    :items="prefList"
                    dense
                    hide-details="auto"
                    item-text="text"
                    item-value="value"
                    label="都道府県"
                    outlined
                    persistent-placeholder
                    placeholder="例）東京都"
                  >
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_address_1`"
                  class="requiredMark"
                  name="市区町村"
                  rules="required|full_width|max:20"
                >
                  <v-text-field
                    v-model="manifest.emission_address_1"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    required
                    placeholder="例）中央区"
                  >
                    <template v-slot:label>
                      <span>市区町村</span>
                      <span class="ml-1">
                        ({{ manifest.emission_address_1?.length || 0 }} / 20)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /都道府県／市区町村 -->
            <!-- 町域 -->
            <v-row>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_address_2`"
                  name="町域"
                  rules="full_width|max:40"
                >
                  <v-text-field
                    v-model="manifest.emission_address_2"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）日本橋兜町"
                  >
                    <template v-slot:label>
                      <span>町域</span>
                      <span class="ml-1">
                        ({{ manifest.emission_address_2?.length || 0 }} / 40)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /町域 -->
            <!-- 詳細住所 -->
            <v-row>
              <v-col md="9">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_address_3`"
                  name="詳細住所"
                  rules="max:50"
                >
                  <v-text-field
                    v-model="manifest.emission_address_3"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）１−２−３　○○ビル○階"
                  >
                    <template v-slot:label>
                      <span>詳細住所</span>
                      <span class="ml-1">
                        ({{ manifest.emission_address_3?.length || 0 }} / 50)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /詳細住所 -->
            <!-- 電話番号 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_phone_number`"
                  name="電話番号"
                  rules="numeric_hyphen_only|min:2|max:15"
                >
                  <v-text-field
                    v-model="manifest.emission_phone_number"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    hint="電話番号を「-」ハイフン「( )」込みで入力します"
                    placeholder="例）000-0000-0000"
                  >
                    <template v-slot:label>
                      <span>電話番号</span>
                      <span class="ml-1">
                        ({{ manifest.emission_phone_number?.length || 0 }} / 15)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /電話番号 -->
          </v-card-text>
          <v-divider class="mt-2" />
          <v-card-subtitle> 事業場を登録 </v-card-subtitle>
          <v-card-text>
            <!-- 事業場名 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_location_name`"
                  class="requiredMark"
                  name="事業場名"
                  rules="required|max:60"
                >
                  <v-text-field
                    v-model="manifest.emission_location_name"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）○○○○○株式会社"
                    required
                  >
                    <template v-slot:label>
                      <span>事業場名</span>
                      <span class="ml-1">
                        ({{ manifest.emission_location_name?.length || 0 }} / 60)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <v-btn
                  color="primary"
                  @click="copyEmissionToEmissionLocationButtonClickEvent"
                >
                  排出事業者情報を事業場にコピー
                </v-btn>
              </v-col>
            </v-row>
            <!-- /事業場名 -->
            <!-- 郵便番号 -->
            <v-row>
              <v-col cols="12" md="5">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_location_zip_code`"
                  class="requiredMark"
                  name="郵便番号"
                  rules="required|zip_code|max:8"
                >
                  <v-text-field
                    v-model="manifest.emission_location_zip_code"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    hint="ハイフンを含めて入力してください"
                    outlined
                    persistent-placeholder
                    placeholder="例）111-1111"
                  >
                    <template v-slot:label>
                      <span>郵便番号</span>
                      <span class="ml-1">
                        ({{ manifest.emission_location_zip_code?.length || 0 }}
                        / 8)
                      </span>
                    </template>
                    <template v-slot:append-outer>
                      <zip-code-search
                        :zip-code="manifest.emission_location_zip_code"
                        @zipAddress="setEmissionLocationZipAddress"
                      />
                      &nbsp;
                      <v-btn
                        outlined
                        small
                        @click="searchFreeEmissionLocationZipButtonClickEvent"
                      >
                        住所→郵便番号
                      </v-btn>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /郵便番号 -->
            <!-- 都道府県／市区町村 -->
            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true }"
                  :vid="`free_emission_location_prefecture_code`"
                  class="requiredMark"
                  name="都道府県"
                >
                  <v-autocomplete
                    v-model="manifest.emission_location_prefecture_code"
                    :error-messages="errors"
                    :items="prefList"
                    dense
                    hide-details="auto"
                    item-text="text"
                    item-value="value"
                    label="都道府県"
                    outlined
                    persistent-placeholder
                    placeholder="例）東京都"
                  >
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_location_address_1`"
                  class="requiredMark"
                  name="市区町村"
                  rules="required|full_width|max:20"
                >
                  <v-text-field
                    v-model="manifest.emission_location_address_1"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）中央区"
                  >
                    <template v-slot:label>
                      <span>市区町村</span>
                      <span class="ml-1">
                        ({{ manifest.emission_location_address_1?.length || 0 }} / 20)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /都道府県／市区町村 -->
            <!-- 町域 -->
            <v-row>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_location_address_2`"
                  name="町域"
                  rules="full_width|max:40"
                >
                  <v-text-field
                    v-model="manifest.emission_location_address_2"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）日本橋兜町"
                  >
                    <template v-slot:label>
                      <span>町域</span>
                      <span class="ml-1">
                        ({{ manifest.emission_location_address_2?.length || 0 }} / 40)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /町域 -->
            <!-- 詳細住所 -->
            <v-row>
              <v-col md="9">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_location_address_3`"
                  name="事業場 詳細住所"
                  rules="max:50"
                >
                  <v-text-field
                    v-model="manifest.emission_location_address_3"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）１−２−３　○○ビル○階"
                  >
                    <template v-slot:label>
                      <span>詳細住所</span>
                      <span class="ml-1">
                        ({{ manifest.emission_location_address_3?.length || 0 }} / 50)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /詳細住所 -->
            <!-- 電話番号 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_emission_location_phone_number`"
                  name="電話番号"
                  rules="numeric_hyphen_only|min:2|max:15"
                >
                  <v-text-field
                    v-model="manifest.emission_location_phone_number"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    hint="電話番号を「-」ハイフン「( )」込みで入力します"
                    outlined
                    persistent-placeholder
                    placeholder="例）000-0000-0000"
                  >
                    <template v-slot:label>
                      <span>電話番号</span>
                      <span class="ml-1">
                        ({{ manifest.emission_location_phone_number?.length || 0 }} / 15)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /電話番号 -->
          </v-card-text>
        </template>

        <!-- 引渡し日 -->
        <v-card-title class="pt-0">
          <span style="color: #0074ba">引渡し日</span>
        </v-card-title>
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="manifest.collected_date"
                :minimum-date="collectedMinimumDate"
                :maximum-date="collectedMaximumDate"
                @dateChangedCallback="collectedDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :class="{requiredMark: manifest.manifest_type !== 'dxe_form' }"
                    :rules="{
                      required: manifest.manifest_type !== 'dxe_form',
                      rangeInDate: {
                        manifest_type: manifest.manifest_type,
                        min: collectedMinimumDate,
                        max: collectedMaximumDate,
                      },
                    }"
                    name="引渡し日"
                  >
                    <v-text-field
                      v-model="manifest.collected_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="引渡し日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /引渡し日 -->

        <!-- マニフェスト区分 -->
        <v-card-title>
          <span style="color: #0074ba">マニフェスト</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-radio-group
                v-model="manifest.manifest_type"
                class="mt-0 pt-0"
                dense
                hide-details="auto"
                row
              >
                <v-radio
                  v-for="n in modifyManifestTypes"
                  :disabled="!n.valid"
                  :key="n.value"
                  :value="n.value"
                >
                  <template v-slot:label>
                    <label
                      class="pa-1"
                      :class="{
                        'radio-selected': manifest.manifest_type === n.value,
                      }"
                      :style="{ 'cursor': n.valid ? 'pointer' : 'not-allowed' }"
                    >
                      {{ n.text }}
                    </label>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /マニフェスト区分 -->

        <!-- 引渡担当者／登録担当者 -->
        <v-card-text class="py-0">
          <template v-if="manifest.form_type === 'master_mode'">
            <v-row>
              <!-- 引渡担当者 -->
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  :class="
                    manifest.manifest_type === 'dxe_form' ? 'requiredMark' : ''
                  "
                  :rules="{
                    required: manifest.manifest_type === 'dxe_form',
                    max: 12,
                  }"
                  :vid="`master_deliver_staff`"
                  name="引渡担当者"
                >
                  <v-combobox
                    v-model="manifest.deliver_staff"
                    :error-messages="errors"
                    :items="emissionDeliverers"
                    :return-object="false"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>引渡担当者</span>
                      <span class="ml-1">
                        ({{ manifest.deliver_staff?.length || 0 }} / 12)
                      </span>
                    </template>
                  </v-combobox>
                </validation-provider>
              </v-col>
              <!-- /引渡担当者-->

              <!-- 登録担当者 -->
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`master_register_staff`"
                  name="登録担当者"
                  rules="max:12"
                >
                  <v-combobox
                    v-model="manifest.register_staff"
                    :error-messages="errors"
                    :items="emissionRegisters"
                    :return-object="false"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>登録担当者</span>
                      <span class="ml-1">
                        ({{ manifest.register_staff?.length || 0 }} / 12)
                      </span>
                    </template>
                  </v-combobox>
                </validation-provider>
              </v-col>
              <!-- /登録担当者 -->
            </v-row>
          </template>

          <template v-if="manifest.form_type === 'free_mode'">
            <v-row>
              <!-- 引渡担当者 -->
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_deliver_staff`"
                  name="引渡担当者"
                  rules="max:12"
                >
                  <v-text-field
                    v-model="manifest.deliver_staff"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）引渡　太郎"
                  >
                    <template v-slot:label>
                      <span>引渡担当者</span>
                      <span class="ml-1">
                        ({{ manifest.deliver_staff?.length || 0 }} / 12)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <!-- /引渡担当者 -->

              <!-- 登録担当者 -->
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  :vid="`free_register_staff`"
                  name="登録担当者"
                  rules="max:12"
                >
                  <v-text-field
                    v-model="manifest.register_staff"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）登録　太郎"
                  >
                    <template v-slot:label>
                      <span>登録担当者</span>
                      <span class="ml-1">
                        ({{ manifest.register_staff?.length || 0 }} / 12)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <!-- /登録担当者 -->
            </v-row>
          </template>
        </v-card-text>
        <!-- 引渡担当者／登録担当者 -->

        <!-- 運搬担当者／運搬方法／車両 -->
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ max: 12 }"
                name="運搬担当者"
              >
                <v-combobox
                  v-model="manifest.transport_user_name"
                  :error-messages="errors"
                  :items="drivers"
                  :return-object="false"
                  clearable
                  dense
                  hide-details="auto"
                  item-value="text"
                  label="運搬担当者"
                  outlined
                  persistent-placeholder
                  @click:clear="transportUserClearClickEvent"
                  @change="transportUserChangeEvent"
                >
                  <template v-slot:label>
                    <span>運搬担当者</span>
                    <span class="ml-1">
                      ({{ manifest.transport_user_name?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider v-slot="{ errors }" name="運搬方法">
                <v-select
                  v-model="manifest.transportation_id"
                  :items="transportations"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="運搬方法"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="車両番号"
                rules="max:15"
              >
                <v-combobox
                  v-model="manifest.vehicle_number"
                  :error-messages="errors"
                  :items="vehicles"
                  :return-object="false"
                  :clearable="true"
                  item-text="number"
                  item-value="number"
                  dense
                  label="車両番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>車両番号</span>
                    <span class="ml-1">
                      ({{ manifest.vehicle_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /運搬担当者／運搬方法／車両 -->

        <v-tabs background-color="blue-grey lighten-4">
          <v-tab>{{ manifest.item_name || "未設定" }}</v-tab>
        </v-tabs>

        <v-card-text class="pt-2">
          <!-- マニフェスト番号／取扱品目／廃棄物の名称 -->
          <v-row>
            <v-col
              v-if="manifest.manifest_type !== 'dxe_form'"
              cols="12"
              md="4"
            >
              <validation-provider
                v-slot="{ errors }"
                name="マニフェスト番号"
                :rules="{
                  numeric: manifest.manifest_type !== 'dxe_form',
                  length: manifest.manifest_type !== 'dxe_form' ? 11 : false,
                }"
              >
                <v-text-field
                  v-model="manifest.manifest_no"
                  :error-messages="errors"
                  :disabled="manifest.manifest_type === 'dxe_form'"
                  :filled="manifest.manifest_type === 'dxe_form'"
                  dense
                  hide-details="auto"
                  label="マニフェスト番号"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>マニフェスト番号</span>
                    <span
                      v-if="manifest.manifest_type !== 'dxe_form'"
                      class="ml-1"
                    >
                      ({{ manifest.manifest_no?.length || 0 }} / 11)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>

            <!-- 取扱品目 -->
            <v-col cols="12" md="4">
              <validation-provider>
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="取扱品目"
                  rules="required"
                >
                  <item-autocomplete
                    v-model="manifest.item_id"
                    :items="items"
                    :error-messages="errors"
                    label="取扱品目"
                    clearable
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="品目名で検索する"
                    @change="itemChangeEvent"
                  />
                </validation-provider>
              </validation-provider>
            </v-col>
            <!-- /取扱品目 -->

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="廃棄物の名称"
                rules="max:30"
              >
                <v-text-field
                  v-model="manifest.waste_name"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="廃棄物の名称"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>廃棄物の名称</span>
                    <span class="ml-1">
                      ({{ manifest.waste_name?.length || 0 }} / 30)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /マニフェスト番号／取扱品目／廃棄物の名称 -->

          <!-- 廃棄物の名称・マニフェスト番号 -->
          <v-row> </v-row>
          <!-- /廃棄物の名称・マニフェスト番号 -->

          <!-- 数量・数量の確定者-->
          <v-row>
            <v-col cols="12" md="2">
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="customMessages"
                :class="
                  manifest.manifest_type === 'dxe_form'
                    ? manifest.quantity_confirm_id === 2 ||
                      manifest.quantity_unit_id > 0
                      ? 'requiredMark'
                      : ''
                    : manifest.quantity_unit_id > 0
                    ? 'requiredMark'
                    : ''
                "
                :rules="{
                  required:
                    (manifest.manifest_type === 'dxe_form' &&
                      (manifest.quantity_confirm_id === 2 ||
                        manifest.quantity_unit_id > 0)) ||
                    (manifest.manifest_type !== 'dxe_form' &&
                      manifest.quantity_unit_id > 0),
                  double: true,
                  min_value: 0.001,
                  max_value: 99999.999,
                  quantityWithDecimal: { int_digits: 5, dec_digits: 3 },
                }"
                name="数量"
              >
                <v-text-field
                  v-model="manifest.quantity"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="数量"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :class="
                  manifest.manifest_type === 'dxe_form'
                    ? manifest.quantity_confirm_id === 2 ||
                      manifest.quantity > 0
                      ? 'requiredMark'
                      : ''
                    : manifest.quantity > 0
                    ? 'requiredMark'
                    : ''
                "
                :rules="{
                  required:
                    manifest.manifest_type === 'dxe_form'
                      ? manifest.quantity_confirm_id === 2 ||
                        manifest.quantity > 0
                      : manifest.quantity > 0,
                }"
                name="数量単位"
              >
                <v-select
                  v-model="manifest.quantity_unit_id"
                  :items="quantityUnits"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="数量単位       "
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                :class="
                  manifest.manifest_type === 'dxe_form' ? 'requiredMark' : ''
                "
                :rules="{
                  required: manifest.manifest_type === 'dxe_form',
                }"
                name="数量の確定者"
              >
                <v-select
                  v-model="manifest.quantity_confirm_id"
                  :items="quantityConfirms"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="数量の確定者"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /数量・数量の確定者-->

          <!-- 荷姿 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :class="
                  manifest.manifest_type === 'dxe_form' ? 'requiredMark' : ''
                "
                :rules="{
                  required: manifest.manifest_type === 'dxe_form',
                }"
                name="荷姿"
              >
                <v-select
                  v-model="manifest.packing_id"
                  :items="packings"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="荷姿"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="2">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ numeric: true, min_value: 0, max_value: 99999 }"
                name="荷姿の数量"
              >
                <v-text-field
                  v-model="manifest.packing_quantity"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="荷姿の数量"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /荷姿 -->

          <!-- 有害物質 -->
          <template v-if="!disableHazardousSubstance">
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: {
                      keys: [
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id,
                      ],
                    },
                  }"
                  name="有害物質1"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_1_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質1"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: {
                      keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id,
                      ],
                    },
                  }"
                  name="有害物質2"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_2_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質2"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: {
                      keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id,
                      ],
                    },
                  }"
                  name="有害物質3"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_3_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質3"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: {
                      keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id,
                      ],
                    },
                  }"
                  name="有害物質4"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_4_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質4"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: {
                      keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_6_id,
                      ],
                    },
                  }"
                  name="有害物質5"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_5_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質5"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                    duplicateKeys: {
                      keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                      ],
                    },
                  }"
                  name="有害物質6"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_6_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質6"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
          </template>
          <!-- /有害物質 -->
        </v-card-text>

        <!-- 運搬ルート -->
        <v-card-text>
          <!-- ルート一覧 -->
          <v-row>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="運搬ルート"
                :rules="{
                  required: true,
                  secondManifestSameDisposalCheck: {
                    level: level,
                    form_type: manifest.form_type,
                    supplier_subscriber_number:
                      manifest.supplier_subscriber_number,
                    disposer_subscriber_number:
                      manifest.disposer_subscriber_number,
                  },
                }"
              >
                <v-input
                  v-model="manifest.transport_route_id"
                  :error-messages="errors"
                  dense
                  label="運搬ルート"
                  outlined
                  readonly
                />
              </validation-provider>
              <v-row>
                <v-col cols="12" md="12">
                  <div class="my-1">
                    <v-text-field
                      v-model="searchByKeyword"
                      append-icon="mdi-magnify"
                      clearable
                      dense
                      hide-details="auto"
                      label="絞り込み検索"
                      outlined
                      placeholder="ルート名・運搬先名・処分先名から検索"
                      persistent-placeholder
                      style="font-size: 90%"
                    />
                  </div>
                </v-col>
              </v-row>
              <div
                v-if="!manifest.can_change_transport_route"
                class="red--text"
              >
                <strong
                >※&nbsp;発注で利用されているためルートの編集はできません。</strong
                >
              </div>
              <v-data-table
                :key="`transport-routes-${reloadKey}`"
                :headers="transportRoutesTableHeaders"
                :items="transportRoutes"
                :item-class="transportRouteRowBackground"
                :items-per-page="-1"
                :search="searchByKeyword"
                hide-default-footer
                single-select
                disable-sort
                fixed-header
                height="23vh"
                class="elevation-1"
                @click:row="transportRowClick"
              >
                <template v-slot:[`item.name`]="{ item }">
                  <div
                    :title="item.name"
                    class="line-clamp one-clamp"
                  >
                    {{ item.name }}
                  </div>
                </template>
                <template v-slot:[`item.transporter_name`]="{ item }">
                  <div
                    :title="item.transporter_name"
                    class="line-clamp one-clamp"
                  >
                    {{ item.transporter_name }}
                  </div>
                </template>
                <template v-slot:[`item.trader_name`]="{ item }">
                  <div
                    :title="item.trader_name"
                    class="line-clamp one-clamp"
                  >
                    {{ item.trader_name }}
                  </div>
                </template>
                <template v-slot:[`item.disposer_location_name`]="{ item }">
                  <div
                    :title="item.disposer_location_name"
                    class="line-clamp one-clamp"
                  >
                    {{ item.disposer_location_name }}
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
          <!-- ルート一覧 -->

          <!-- ルート詳細 -->
          <transport-route-result
            :custom-mode="true"
            :emission="selectedEmission"
            :freeTypeEmission="freeTypeEmission"
            :form="manifest"
            :transport-route-id="manifest.transport_route_id"
            :master-mode="manifest.form_type === 'master_mode'"
          />
          <!-- /ルート詳細 -->
        </v-card-text>
        <!-- /運搬ルート -->

        <!-- 運搬方法／処分方法 -->
        <v-card-text class="py-0">
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="処分方法">
                <v-select
                  v-model="manifest.disposal_method_id"
                  :items="disposalMethods"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="処分方法"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /運搬方法／処分方法 -->

        <!-- 連絡番号／備考 -->
        <v-card-text class="py-0">
          <v-row>
            <!-- 連絡番号 -->
            <v-col cols="12" md="6" class="pa-0">
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    :vid="`master_contact_number_1`"
                    rules="contact_num_only|max:20"
                    name="連絡番号１"
                  >
                    <v-text-field
                      v-model="manifest.contact_number_1"
                      :error-messages="errors"
                      dense
                      label="連絡番号１"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>連絡番号１</span>
                        <span class="ml-1">
                          ({{ manifest.contact_number_1?.length || 0 }} / 20)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                      <template v-slot:append-outer>
                        <v-btn
                          color="primary"
                          fab
                          x-small
                          elevation="0"
                          @click="addContactNumberClick"
                        >
                          <v-icon dark> mdi-plus </v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-expand-transition>
                <v-row v-if="displayContactNumber2">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      :vid="`master_contact_number_2`"
                      rules="contact_num_only|max:20"
                      name="連絡番号２"
                    >
                      <v-text-field
                        v-model="manifest.contact_number_2"
                        :error-messages="errors"
                        dense
                        label="連絡番号２"
                        hide-details="auto"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>連絡番号２</span>
                          <span class="ml-1">
                            ({{ manifest.contact_number_2?.length || 0 }} / 20)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addContactNumberClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
              <v-expand-transition>
                <v-row v-if="displayContactNumber3">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      :vid="`master_contact_number_3`"
                      rules="contact_num_only|max:20"
                      name="連絡番号３"
                    >
                      <v-text-field
                        v-model="manifest.contact_number_3"
                        :error-messages="errors"
                        dense
                        label="連絡番号３"
                        hide-details="auto"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>連絡番号３</span>
                          <span class="ml-1">
                            ({{ manifest.contact_number_3?.length || 0 }} / 20)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            style="visibility: hidden"
                            @click="addContactNumberClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
            </v-col>
            <!-- /連絡番号 -->

            <!-- 備考 -->
            <v-col cols="12" md="6" class="pa-0">
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    name="備考１"
                    rules="max:25"
                  >
                    <v-text-field
                      v-model="manifest.note_1"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="備考１"
                      outlined
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>備考１</span>
                        <span class="ml-1">
                          ({{ manifest.note_1?.length || 0 }} / 25)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                      <template v-slot:append-outer>
                        <v-btn
                          color="primary"
                          fab
                          x-small
                          elevation="0"
                          @click="addNoteClick"
                        >
                          <v-icon dark> mdi-plus </v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-expand-transition>
                <v-row v-if="displayNote2">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考２"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_2"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考２"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考２</span>
                          <span class="ml-1">
                            ({{ manifest.note_2?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
              <v-expand-transition>
                <v-row v-if="displayNote3">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考３"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_3"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考３"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考３</span>
                          <span class="ml-1">
                            ({{ manifest.note_3?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
              <v-expand-transition>
                <v-row v-if="displayNote4">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考４"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_4"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考４"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考４</span>
                          <span class="ml-1">
                            ({{ manifest.note_4?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
              <v-expand-transition>
                <v-row v-if="displayNote5">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考５"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_5"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考５"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考５</span>
                          <span class="ml-1">
                            ({{ manifest.note_5?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            style="visibility: hidden"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
            </v-col>
            <!-- /備考 -->
          </v-row>
        </v-card-text>
        <!-- /連絡番号／備考 -->

        <!-- カスタムラベル -->
        <template v-if="isJwMode">
          <v-card-title class="pt-0">
            <span
              v-if="manifest.custom_label.use_title"
              style="color: #0074ba"
            >{{ manifest.custom_label.name_title }}</span
            >
          </v-card-title>

          <v-card-text class="py-0">
            <v-row>
              <v-col
                v-if="manifest.custom_label.custom_labels1_use"
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels1"
                  :name="
                    manifest.custom_label.custom_labels1_name != null
                      ? manifest.custom_label.custom_labels1_name
                      : 'ラベル①'
                  "
                  :rules="{
                    required: manifest.custom_label.custom_labels1_required,
                    alpha_num_only:
                      manifest.custom_label
                        .custom_labels1_halfsize_alphanumeric,
                    max:
                      manifest.custom_label.custom_labels1_maximum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels1_maximum_characters
                        : 255,
                    min:
                      manifest.custom_label.custom_labels1_minimum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels1_minimum_characters
                        : 0,
                  }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_1"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="
                      manifest.custom_label.custom_labels1_name != null
                        ? manifest.custom_label.custom_labels1_name
                        : 'ラベル①'
                    "
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels1_name != null ? manifest.custom_label.custom_labels1_name : 'ラベル①' }}</span>
                      <span class="ml-1">
                        ({{ manifest.manifest_custom_label.custom_labels_1?.length || 0 }}
                        / {{ manifest.custom_label.custom_labels1_maximum_characters != null ? manifest.custom_label.custom_labels1_maximum_characters : 255 }})
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
                v-if="manifest.custom_label.custom_labels2_use"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels2"
                  :name="
                    manifest.custom_label.custom_labels2_name != null
                      ? manifest.custom_label.custom_labels2_name
                      : 'ラベル②'
                  "
                  :rules="{
                    required: manifest.custom_label.custom_labels2_required,
                    alpha_num_only:
                      manifest.custom_label
                        .custom_labels2_halfsize_alphanumeric,
                    max:
                      manifest.custom_label.custom_labels2_maximum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels2_maximum_characters
                        : 255,
                    min:
                      manifest.custom_label.custom_labels2_minimum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels2_minimum_characters
                        : 0,
                  }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_2"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="
                      manifest.custom_label.custom_labels2_name != null
                        ? manifest.custom_label.custom_labels2_name
                        : 'ラベル②'
                    "
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels2_name != null ? manifest.custom_label.custom_labels2_name : 'ラベル②' }}</span>
                      <span class="ml-1">
                        ({{ manifest.manifest_custom_label.custom_labels_2?.length || 0 }}
                        / {{ manifest.custom_label.custom_labels2_maximum_characters != null ? manifest.custom_label.custom_labels2_maximum_characters : 255 }})
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
                v-if="manifest.custom_label.custom_labels3_use"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels3"
                  :name="
                    manifest.custom_label.custom_labels3_name != null
                      ? manifest.custom_label.custom_labels3_name
                      : 'ラベル③'
                  "
                  :rules="{
                    required: manifest.custom_label.custom_labels3_required,
                    alpha_num_only:
                      manifest.custom_label
                        .custom_labels3_halfsize_alphanumeric,
                    max:
                      manifest.custom_label.custom_labels3_maximum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels3_maximum_characters
                        : 255,
                    min:
                      manifest.custom_label.custom_labels3_minimum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels3_minimum_characters
                        : 0,
                  }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_3"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="
                      manifest.custom_label.custom_labels3_name != null
                        ? manifest.custom_label.custom_labels3_name
                        : 'ラベル③'
                    "
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels3_name != null ? manifest.custom_label.custom_labels3_name : 'ラベル③' }}</span>
                      <span class="ml-1">
                        ({{ manifest.manifest_custom_label.custom_labels_3?.length || 0 }}
                        / {{ manifest.custom_label.custom_labels3_maximum_characters != null ? manifest.custom_label.custom_labels3_maximum_characters : 255 }})
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
                v-if="manifest.custom_label.custom_labels4_use"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels4"
                  :name="
                    manifest.custom_label.custom_labels4_name != null
                      ? manifest.custom_label.custom_labels4_name
                      : 'ラベル④'
                  "
                  :rules="{
                    required: manifest.custom_label.custom_labels4_required,
                    alpha_num_only:
                      manifest.custom_label
                        .custom_labels4_halfsize_alphanumeric,
                    max:
                      manifest.custom_label.custom_labels4_maximum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels4_maximum_characters
                        : 255,
                    min:
                      manifest.custom_label.custom_labels4_minimum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels4_minimum_characters
                        : 0,
                  }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_4"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="
                      manifest.custom_label.custom_labels4_name != null
                        ? manifest.custom_label.custom_labels4_name
                        : 'ラベル④'
                    "
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels4_name != null ? manifest.custom_label.custom_labels4_name : 'ラベル④' }}</span>
                      <span class="ml-1">
                        ({{ manifest.manifest_custom_label.custom_labels_4?.length || 0 }}
                        / {{ manifest.custom_label.custom_labels4_maximum_characters != null ? manifest.custom_label.custom_labels4_maximum_characters : 255 }})
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
                v-if="manifest.custom_label.custom_labels5_use"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels5"
                  :name="
                    manifest.custom_label.custom_labels5_name != null
                      ? manifest.custom_label.custom_labels5_name
                      : 'ラベル⑤'
                  "
                  :rules="{
                    required: manifest.custom_label.custom_labels5_required,
                    alpha_num_only:
                      manifest.custom_label
                        .custom_labels5_halfsize_alphanumeric,
                    max:
                      manifest.custom_label.custom_labels5_maximum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels5_maximum_characters
                        : 255,
                    min:
                      manifest.custom_label.custom_labels5_minimum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels5_minimum_characters
                        : 0,
                  }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_5"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="
                      manifest.custom_label.custom_labels5_name != null
                        ? manifest.custom_label.custom_labels5_name
                        : 'ラベル⑤'
                    "
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels5_name != null ? manifest.custom_label.custom_labels5_name : 'ラベル⑤' }}</span>
                      <span class="ml-1">
                        ({{ manifest.manifest_custom_label.custom_labels_5?.length || 0 }}
                        / {{ manifest.custom_label.custom_labels5_maximum_characters != null ? manifest.custom_label.custom_labels5_maximum_characters : 255 }})
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
                v-if="manifest.custom_label.custom_labels6_use"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels6"
                  :name="
                    manifest.custom_label.custom_labels6_name != null
                      ? manifest.custom_label.custom_labels6_name
                      : 'ラベル⑥'
                  "
                  :rules="{
                    required: manifest.custom_label.custom_labels6_required,
                    alpha_num_only:
                      manifest.custom_label
                        .custom_labels6_halfsize_alphanumeric,
                    max:
                      manifest.custom_label.custom_labels6_maximum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels6_maximum_characters
                        : 255,
                    min:
                      manifest.custom_label.custom_labels6_minimum_characters !=
                      null
                        ? manifest.custom_label
                            .custom_labels6_minimum_characters
                        : 0,
                  }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_6"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="
                      manifest.custom_label.custom_labels6_name != null
                        ? manifest.custom_label.custom_labels6_name
                        : 'ラベル⑥'
                    "
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels6_name != null ? manifest.custom_label.custom_labels6_name : 'ラベル⑥' }}</span>
                      <span class="ml-1">
                        ({{ manifest.manifest_custom_label.custom_labels_6?.length || 0 }}
                        / {{ manifest.custom_label.custom_labels6_maximum_characters != null ? manifest.custom_label.custom_labels6_maximum_characters : 255 }})
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>
        </template>

        <!-- 紙マニフェスト返却日 -->
        <template
          v-if="manifest.manifest_type === 'paper_form' && manifest.paper_manifest_return_vote"
        >
          <v-card-title class="pt-0">
            <span style="color: #0074ba">紙マニフェスト返却日</span>
          </v-card-title>
          <v-card-text>
            <!-- A票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.a_return_date"
                  @dateChangedCallback="paperManifestReturnDateAChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="A票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.a_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="A票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="A票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.a_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>A票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.a_return_target?.length || 0 }}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /A票 -->
            <!-- B2票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b2_return_date"
                  @dateChangedCallback="paperManifestReturnDateB2ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B2票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b2_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B2票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B2票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b2_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B2票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{manifest.paper_manifest_return_vote.b2_return_target?.length || 0}}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B2票 -->
            <!-- B4票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b4_return_date"
                  @dateChangedCallback="paperManifestReturnDateB4ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B4票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b4_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B4票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B4票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b4_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B4票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.b4_return_target?.length || 0}}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B4票 -->
            <!-- B6票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b6_return_date"
                  @dateChangedCallback="paperManifestReturnDateB6ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B6票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b6_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B6票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B6票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b6_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B6票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.b6_return_target?.length || 0 }}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B6票 -->
            <!-- D票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.d_return_date"
                  @dateChangedCallback="paperManifestReturnDateDChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="D票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.d_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="D票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="D票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.d_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>D票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.d_return_target ?.length || 0 }}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /D票 -->
            <!-- E票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.e_return_date"
                  @dateChangedCallback="paperManifestReturnDateEChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="E票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.e_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="E票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="E票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.e_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="E票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>E票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.e_return_target?.length || 0}}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /E票 -->
            <!-- B1票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b1_return_date"
                  @dateChangedCallback="paperManifestReturnDateB1ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B1票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b1_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B1票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B1票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="
                      manifest.paper_manifest_return_vote.b1_return_target
                    "
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B1票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.b1_return_target?.length || 0 }}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B1票 -->
            <!-- C1票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.c1_return_date"
                  @dateChangedCallback="paperManifestReturnDateC1ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="C1票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.c1_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="C1票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="C1票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.c1_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>C1票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.c1_return_target?.length || 0 }}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /C1票 -->
            <!-- C2票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.c2_return_date"
                  @dateChangedCallback="paperManifestReturnDateC2ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="C2票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.c2_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="C2票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="C2票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.c2_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>C2票&emsp;返却先</span>
                      <span class="ml-1">
                        ({{ manifest.paper_manifest_return_vote.c2_return_target ?.length || 0 }}&nbsp;/&nbsp;60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /C2票 -->
          </v-card-text>
        </template>
        <!-- /紙マニフェスト返却日 -->

        <!-- マニフェスト検索キーワード -->
        <v-card-text>
          <v-row>
            <v-col cols="12" md="12">
              <validation-provider
                v-slot="{ errors }"
                name="マニフェスト検索キーワード"
                rules="max:25"
              >
                <v-text-field
                  v-model="manifest.manifest_search_keyword"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="マニフェスト検索キーワード"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    マニフェスト検索キーワード
                    <span class="ml-1">
                      ({{ manifest.manifest_search_keyword?.length || 0 }}&nbsp;/&nbsp;25)
                    </span>
                    <span class="red--text font-weight-bold"
                    >&emsp;※&nbsp;DXEStation排出モードにも表示されます。&nbsp;</span
                    >
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /マニフェスト検索キーワード -->
        <v-card-text class="text-right mt-2">
          引渡日: {{ formattedCollectedDate() }}&nbsp;／&nbsp;マニフェスト区分: {{ manifestTypeLabel() }}
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="cancelButtonClickEvent"> 閉じる </v-btn>
          <VButtonProcessing
            v-if="manifest.manifest_type === 'dxe_form'"
            :onclick="reservationRegisterButtonClickEvent"
            :disabled="!manifest"
            :key="`reservationRegisterButton-${reloadKey}`"
            color="primary"
            elevation="5"
          >
            予約登録
          </VButtonProcessing>
          <template v-if="manifest.manifest_type === 'dxe_form'">
            <VButtonProcessing
              :onclick="receptionRegisterButtonClickEvent"
              :disabled="receptionRegisterDisableControl"
              :key="`receptionRegisterButton-${reloadKey}`"
              color="primary"
              elevation="5"
            >
              本登録
            </VButtonProcessing>
          </template>

          <VButtonProcessing
            v-if="manifest.manifest_type !== 'dxe_form'"
            :onclick="registerButtonClickEvent"
            :disabled="!manifest"
            :key="`registerButton-${reloadKey}`"
            color="primary"
            elevation="5"
          >
            登録
          </VButtonProcessing>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <search-emission-dialog
      :enable-dialog="enableSearchEmissionDialog"
      emission-filter="exclude_general"
      @cancelCallback="searchDialogCancelCallback"
      @registerCallback="reflectEmissionCallback"
    />
    <search-tenant-disposer-dialog
      :enable-dialog="enableSearchTenantDisposerDialog"
      @cancelCallback="searchDialogCancelCallback"
      @registerCallback="reflectTenantDisposerCallback"
    />
    <!-- スポット事業場入力モーダル -->
    <spot-emission-location-dialog
      :enable-dialog="enableEditSpotLocation"
      :master-emission="this.selectedEmission"
      :location="spotLocation"
      @closeCallback="spotLocationCancelCallback"
      @registerCallback="spotLocationRegisterCallback"
    />
    <!-- /スポット事業場入力モーダル -->

    <v-snackbar
      v-model="manifestTypeMismatchAlert"
      :timeout="-1"
      color="error"
      width="900"
    >
      <div v-html="manifestTypeMismatchMessage"></div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="manifestTypeMismatchAlert = false">
          閉じる
        </v-btn>
      </template>
    </v-snackbar>
    <search-emission-zip-modal
      :enable="enableSearchFreeEmissionZipModal"
      :initial-prefecture-code="manifest.emission_prefecture_code"
      :initial-city-name="manifest.emission_address_1"
      :initial-town-name="manifest.emission_address_2"
      @reflectFullCallback="reflectFreeEmissionFullCallback"
      @reflectZipCallback="reflectFreeEmissionZipCallback"
      @closeCallback="closeModalCallback"
    />
    <search-emission-location-zip-modal
      :enable="enableSearchFreeEmissionLocationZipModal"
      :initial-prefecture-code="manifest.emission_location_prefecture_code"
      :initial-city-name="manifest.emission_location_address_1"
      :initial-town-name="manifest.emission_location_address_2"
      @reflectFullCallback="reflectFreeEmissionLocationFullCallback"
      @reflectZipCallback="reflectFreeEmissionLocationZipCallback"
      @closeCallback="closeModalCallback"
    />
    <confirm ref="confirm" />

  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ZipCodeSearch from "../../../../components/ZipCodeSearch";
import PrefectureList from "../../../../mixins/PrefectureList";
import TransportRouteResult from "../../../components/TransportRouteResult";
import VButtonProcessing from "../../../../components/VButtonProcessing";
import SearchEmissionDialog from "../../dialogs/_SearchEmissionDialog";
import SearchTenantDisposerDialog from "../../dialogs/_SearchTenantDisposerDialog";
import SpotEmissionLocationDialog from "../../dialogs/_SpotEmissionLocationDialog.vue";
import SearchEmissionZipModal from "../../../components/SearchZipModal.vue";
import SearchEmissionLocationZipModal from "../../../components/SearchZipModal.vue";
import InfiniteEmissionAndLocationAutoComplete from "../../../components/InfiniteEmissionAndLocationAutoComplete.vue";
import ManifestHistory from './_ManifestHistory.vue'
import ItemAutocomplete  from "@/driver/components/ItemAutocomplete.vue";

import _ from "lodash";
import InfiniteTenantTraderAndLocationAutoComplete
  from "@/driver/components/InfiniteTenantTraderAndLocationAutoComplete.vue";
import Confirm from "@/components/Confirm.vue";

export default {
  name: "ManifestManagementForm",
  components: {
    Confirm,
    InfiniteTenantTraderAndLocationAutoComplete,
    VButtonProcessing,
    ZipCodeSearch,
    TransportRouteResult,
    SearchEmissionDialog,
    SearchTenantDisposerDialog,
    SpotEmissionLocationDialog,
    SearchEmissionZipModal,
    SearchEmissionLocationZipModal,
    InfiniteEmissionAndLocationAutoComplete,
    ManifestHistory,
    ItemAutocomplete,
  },
  mixins: [PrefectureList],
  props: {},
  data() {
    return {
      enableEditSpotLocation: false,
      enableSearchFreeEmissionZipModal: false,
      enableSearchFreeEmissionLocationZipModal: false,
      systemDate: this.$dayjs().format("YYYY-MM-DD"),

      customMessages: {
        double: "数量は半角数字のみ使用できます",
      },
      /**
       * @property manifest { Object }
       * @property manifest.supplierable_id { Number }
       * @property manifest.supplierable_type { String }
       * @property.manifest_custom_label { Object }
       * @property manifest.can_change_transport_route { Boolean } 排出事業者変更
       * @property manifest.effective_transport_route_include_non_subscribe_trader
       */
      manifest: null,
      spotLocation: null,
      searchByKeyword: null,
      /** 選択された排出事業者情報 */
      selectedEmission: null,
      backupSelectedEmission: null,
      /** マニフェスト区分 */
      manifestTypes: null,
      /** 引渡担当者 */
      emissionDeliverers: [],
      /** 登録担当者 */
      emissionRegisters: [],
      /** 品目 */
      items: [],
      /** 荷姿 */
      packings: [],
      /** 有害物質 */
      hazardousSubstances: [],
      /** 運搬方法 */
      transportations: [],
      /** 数量単位 */
      quantityUnits: [],
      /** 処分方法 */
      disposalMethods: [],
      /** 数量確定者 */
      quantityConfirms: [],
      /** 運搬ルート */
      transportRoutes: [],
      /** 収集運搬業者 */
      traders: [],
      /** 処分業者 */
      disposers: [],
      drivers: [],
      vehicles: [],
      /** レベル */
      level: null,

      /** 排出事業場一覧ヘッダー */
      tableHeaders: [
        { value: "name", text: "事業場名" },
        { value: "address", text: "住所" },
        { value: "phone_number", text: "電話番号" },
      ],
      transportRoutesTableHeaders: [
        { value: "name", text: "ルート名称", width: "25%" },
        { value: "transporter_name", text: "区間１収集運搬業者", width: "25%" },
        { value: "trader_name", text: "区間１運搬先会社名", width: "25%" },
        { value: "disposer_location_name", text: "処分業者事業場名", width: "25%" },
      ],
      transportSelected: [],
      voteStaffs: [],
      selectedTransportRoute: null,
      displayContactNumber2: false,
      displayContactNumber3: false,
      displayNote2: false,
      displayNote3: false,
      displayNote4: false,
      displayNote5: false,
      enableSearchEmissionDialog: false,
      enableSearchTenantDisposerDialog: false,
      manifestTypeMismatchAlert: false,
      manifestTypeMismatchMessage: null,
      reloadKey: 0,
      selectableSupplierIds: [],
      stopLocationAutoSelect: false,
    };
  },
  computed: {
    ...mapState("DriverTenantConfiguration", {
      isJwMode: "use_jw_mode",
      mode: "mode",
    }),

    isEdit () {
      return !!this.$route.params.id;
    },

    /**
     * 本登録ボタンの制御
     * @returns {boolean} true: 押下不可
     */
    receptionRegisterDisableControl() {
      // マニフェストのオブジェクトが存在しない場合は押下不可
      if (!this.manifest) {
        return true;
      }
      // 排出承認の場合は押下不可
      if (this.manifest.approval_type === "use_approval") {
        return true;
      }
      // 引渡し日が未入力の場合は押下不可
      if (!this.manifest.collected_date) {
        return true;
      }
      // 引渡し日が未来日の場合は押下不可
      if (this.systemDate < this.manifest.collected_date) {
        return true;
      }
      return false;
    },
    disableHazardousSubstance() {
      let item = this.items.find((v) => v.value === this.manifest.item_id);

      if (_.isUndefined(item)) {
        this.removeHazardousSubstances();
        return true;
      }
      if (!item.use_harmful) {
        this.removeHazardousSubstances();
      }
      return !item.use_harmful;
    },
    /**
     * マニフェスト種別を返す.
     */
    modifyManifestTypes() {
      return this.buildManifestTypeMembers();
    },
    collectedMinimumDate() {
      return this.$dayjs(this.manifest.created_date)
        .subtract(10, "years")
        .format("YYYY-MM-DD");
    },
    collectedMaximumDate() {
      return this.$dayjs(this.manifest.created_date)
        .add(5, "years")
        .format("YYYY-MM-DD");
    },
    /**
     * 排出持込時のフリーモードで入力された排出事業者情報
     */
    freeTypeEmission () {
      if (this.manifest.form_type === 'free_mode') {
        return {
          emission_name: this.manifest.emission_name,
          emission_zip_code: this.manifest.emission_zip_code,
          emission_prefecture_code: this.manifest.emission_prefecture_code,
          emission_address_1: this.manifest.emission_address_1,
          emission_address_2: this.manifest.emission_address_2,
          emission_address_3: this.manifest.emission_address_3,
          emission_phone_number: this.manifest.emission_phone_number,
          subscriber_number: null,
        }
      }
      return {}
    },
  },

  watch: {

    /**
     * 排出事業者選択による制御
     * 1次マニの場合のみ動作.
     * @param newVal
     */
    selectedEmission(newVal) {
      if (this.level === "first_manifest") {
        if (newVal === null) {
          this.emissionDeliverers = [];
          this.emissionRegisters = [];
        } else {
          const emissionLocationId = this.manifest.use_spot_emission_location
            ? null
            : newVal.location?.id;
          if (newVal.id != null) {
            this.searchEmissionDeliverersBy(newVal.id, emissionLocationId);
            this.searchEmissionRegistersBy(newVal.id, emissionLocationId);
          } else if (newVal.supplierable_id != null) {
            this.searchEmissionDeliverersBy(
              newVal.supplierable_id,
              emissionLocationId
            );
            this.searchEmissionRegistersBy(
              newVal.supplierable_id,
              emissionLocationId
            );
          }
        }
      }
    },

    /**
     * 運搬ルートの整合性チェック
     * 選択中の運搬ルートが有効か確認し、無効ならエラーメッセージを表示して選択を解除する
     */
    transportRoutes(routeList) {
      if (!routeList || routeList.length === 0) return;

      const selectedRouteId = this.manifest.transport_route_id;
      if (!selectedRouteId) return;
      // ルートが一覧に存在するか
      const exists = routeList.find(r => r.id === selectedRouteId);
      if (!exists) {
        this.showError({message: '選択中のルートは対象品目外のため解除されました'});
        this.manifest.transport_route_id = null;
        this.manifest.disposal_method_id = null;
        this.manifest.include_non_subscribe_trader = false;
      }
    }
  },

  created() {
    this.fetchItems().then((content) => {
      this.items = content.items;
    });
    this.fetchQuantityUnits().then((content) => {
      this.quantityUnits = content.quantity_units;
    });
    this.fetchQuantityConfirms().then((content) => {
      this.quantityConfirms = content.quantity_confirms;
    });
    this.fetchPackings().then((content) => {
      this.packings = content.packings;
    });
    this.fetchHazardousSubstances().then((content) => {
      this.hazardousSubstances = content.hazardous_substances;
    });
    this.fetchTransportations().then((content) => {
      this.transportations = content.transportations;
    });
    this.fetchDisposalMethods().then((content) => {
      this.disposalMethods = content.disposal_methods;
    });
    this.fetchDrivers({without_invisible: true}).then((content) => {
      this.drivers = content.drivers;
    });
    this.fetchVehicles({ registered_number: true }).then((content) => {
      this.vehicles = content.vehicles;
    });

    this.fetchVoteStaffs({without_invisible: true}).then((content) => {
      this.voteStaffs = content.vote_staffs;
    });
    this.searchSecondManifestUsers();

    // 複写の場合
    if (this.$route.params.duplicate_id) {
      this.fetchDuplicateManifest({ id: this.$route.params.duplicate_id }).then(
        (content) => {
          this.manifestTypes = content.manifest_types;
          // this.stopLocationAutoSelect = true;
          this.manifest = content;
          this.level = content.level;
          this.selectedEmission = content.emission_details;
          this.backupSelectedEmission = _.cloneDeep(content.emission_details);
          this.controlNotes();
          this.controlContactNumbers();
          this.setTransportRoutes();
          this.duplicateSetting();
          if (this.$route.query.plan_date) {
            this.manifest.collected_date = this.$route.query.plan_date;
          }
          if (this.level === "first_manifest") {
            this.initValidateManifestTypeState(true);
          }

        }
      );
      // 更新の場合
    } else if (this.$route.params.id) {
      this.fetchEditManifest({ id: this.$route.params.id }).then((content) => {
        this.manifestTypes = content.manifest_types;
        this.selectedEmission = content.emission_details;
        this.backupSelectedEmission = _.cloneDeep(content.emission_details);
        this.manifest = content;
        this.level = content.level;
        this.controlNotes();
        this.controlContactNumbers();
        this.setTransportRoutes();
        if (this.level === "first_manifest") {
          this.initValidateManifestTypeState(true);
        }
      });
      // 新規の場合
    } else {
      this.fetchNewManifest().then((content) => {
        this.manifestTypes = content.manifest_types;
        this.manifest = content;
        this.level = this.$route.params.level;
        this.manifest.level = this.level;
        this.controlNotes();
        this.controlContactNumbers();
        this.setTransportRoutes();
        if (this.$route.query.plan_date) {
          this.manifest.collected_date = this.$route.query.plan_date;
        }

        this.$nextTick(() => {
          this.$refs.emission.focusToSupplier();
        })

      });
    }
  },
  methods: {
    ...mapActions("DriverMasterSearchEmissionDetail", {
      fetchEmissionDetail: "show",
    }),
    ...mapActions("snackbar", {
      showInfo: "showInfo",
    }),
    ...mapActions("DriverManifestManagement", {
      fetchNewManifest: "new",
      fetchEditManifest: "edit",
      createManifest: "create",
      updateManifest: "update",
    }),
    ...mapActions("DriverManifestManagementRegistration", {
      createManifestRegistration: "create",
      updateManifestRegistration: "update",
    }),
    ...mapActions("DriverManifestManagementReservation", {
      createManifestReservation: "create",
      updateManifestReservation: "update",
    }),
    ...mapActions("DriverManifestManagementDuplicate", {
      fetchDuplicateManifest: "show",
    }),
    ...mapActions("DriverMasterSearchItem", {
      fetchItems: "index",
    }),
    ...mapActions("DriverMasterSearchPacking", {
      fetchPackings: "index",
    }),
    ...mapActions("DriverMasterSearchHazardousSubstance", {
      fetchHazardousSubstances: "index",
    }),
    ...mapActions("DriverMasterSearchTransportation", {
      fetchTransportations: "index",
    }),
    ...mapActions("DriverMasterSearchQuantityUnit", {
      fetchQuantityUnits: "index",
    }),
    ...mapActions("DriverMasterSearchDisposalMethod", {
      fetchDisposalMethods: "index",
    }),
    ...mapActions("DriverMasterSearchQuantityConfirm", {
      fetchQuantityConfirms: "index",
    }),
    ...mapActions("DriverMasterSearchTransportRoute", {
      fetchTransportRoutes: "search",
      fetchTransportRoute: "show",
    }),
    ...mapActions("DriverMasterSearchEmissionDeliverer", {
      fetchDeliverers: "index",
    }),
    ...mapActions("DriverMasterSearchEmissionRegister", {
      fetchRegisters: "index",
    }),
    ...mapActions("DriverMasterSearchTrader", {
      fetchTraders: "index",
    }),
    ...mapActions("DriverMasterSearchDriver", {
      fetchDrivers: "index",
    }),
    ...mapActions("DriverMasterSearchVehicle", {
      fetchVehicles: "search",
    }),
    ...mapActions("DriverMasterSearchVoteStaff", {
      fetchVoteStaffs: "index",
    }),
    ...mapActions("DriverMasterSearchUser", {fetchUsers: "index"}),
    ...mapActions("snackbar", {
      showError: "showError",
    }),
    ...mapActions("DriverMasterMemberTenantDisposerLocation", {
      fetchDisposerDetail: "show",
    }),

    /**
     * 排出チェンジ コールバック
     * @param emission_id { Number } 事業者ID
     * @param location_id { Number } 事業場ID
     * @param location_code { String } 事業場コード
     */
    supplierChangeCallback(emission_id, location_id, location_code) {
      const backup_supplier_id = this.manifest.supplierable_id;
      const backup_location_id = this.manifest.supplierable_location_id;
      const backup_location_code = this.manifest.supplierable_location_code;

      this.manifest.supplierable_id = emission_id;
      this.manifest.supplierable_type = this.level === 'first_manifest' ? "Emission" : "Trader"
      if (emission_id !== backup_supplier_id) {
        this.manifest.use_spot_emission_location = false;
      }

      this.manifest.supplierable_location_id = location_id;
      this.manifest.supplierable_location_code = location_code;

      if (backup_location_id === location_id && backup_location_code && location_code === null) {
        this.manifest.supplierable_location_code = backup_location_code;
      }

      if (!this.manifest.use_spot_emission_location && (backup_supplier_id !== emission_id || backup_location_id !== location_id)) {
        this.$nextTick(() => {
          this.loadSupplierDetail();
        })
      }
      this.reloadKey++;
    },

    /**
     * 選択した排出事業者・事業場の詳細情報を取得.
     */
    loadSupplierDetail() {
      this.selectedEmission = null;
      if (!this.manifest.supplierable_id) {
        return;
      }
      if (this.level === 'first_manifest') {
        this.fetchEmissionDetail({ emission_id: this.manifest.supplierable_id, emission_location_id: this.manifest.supplierable_location_id }).then((content) => {
          this.reflectEmissionDetail(content);
        });
      } else {
        this.fetchDisposerDetail({ id: this.manifest.supplierable_location_id, trader_id: this.manifest.supplierable_id }).then((content) => {
          this.reflectTenantDisposerDetail(content);
        });
      }
    },

    /**
     * 排出事業者(1次マニ)を選択 コールバック
     * @param emissionDetail
     */
    reflectEmissionCallback(emissionDetail) {
      this.manifest.supplierable_id = emissionDetail.id;
      this.manifest.supplierable_type = "Emission";
      this.manifest.supplierable_location_id = emissionDetail.location.id;
      this.manifest.supplierable_location_code = emissionDetail.location.code;
      this.manifest.use_spot_emission_location = false;
      this.loadSupplierDetail();
      this.searchDialogCancelCallback();
    },

    /**
     * 排出事業者(2次マニ)を選択 コールバック
     * @param emissionDetail
     */
    reflectTenantDisposerCallback(emissionDetail) {
      this.manifest.supplierable_id = emissionDetail.id;
      this.manifest.supplierable_type = "Trader";
      this.manifest.supplierable_location_id = emissionDetail.location.id;
      this.manifest.supplierable_location_code = emissionDetail.location.code;
      this.loadSupplierDetail();
      this.searchDialogCancelCallback();
    },

    /**
     * 排出の詳細情報を画面に反映
     * @param emissionDetail { Object }
     */
    reflectEmissionDetail(emissionDetail) {
      const chosenManifestType = this.manifest.manifest_type;

      this.selectedEmission = emissionDetail;
      this.backupSelectedEmission = _.cloneDeep(emissionDetail);
      this.manifest.use_spot_emission_location = false;
      this.manifest.lock_spot_emission_location = false;
      /** sales-item-blockに渡す引数更新 */

      this.manifest.supplierable_location_code = emissionDetail.location.code;
      this.manifest.approval_type = emissionDetail.approval_type;
      this.manifest.emission_location_name = null;
      this.manifest.emission_location_zip_code = null;
      this.manifest.emission_location_prefecture_code = null;
      this.manifest.emission_location_address_1 = null;
      this.manifest.emission_location_address_2 = null;
      this.manifest.emission_location_address_3 = null;
      this.manifest.emission_location_phone_number = null;

      // 変更前のマニフェスト区分がEDIで、選択された排出事業者のマニフェスト区分がEDI以外であれば書き換え
      if (chosenManifestType === 'dxe_form' && emissionDetail && emissionDetail?.manifest_type !== 'dxe_form') {
        this.manifest.manifest_type = emissionDetail.manifest_type;
        this.showChangedManifestTypeMessage(chosenManifestType);
      }

      this.searchDialogCancelCallback();
    },

    /**
     * 排出の詳細情報を画面に反映
     * @param emissionDetail { Object }
     */
    reflectTenantDisposerDetail(emissionDetail) {
      const chosenManifestType = this.manifest.manifest_type;

      this.selectedEmission = emissionDetail;

      /** sales-item-blockに渡す引数更新 */
      this.manifest.supplierable_type = "Trader";
      this.manifest.supplier_subscriber_number = emissionDetail?.subscriber_number;
      this.manifest.supplierable_location_code = emissionDetail?.location?.code;

      // 変更前のマニフェスト区分がEDIで、選択された排出事業者のマニフェスト区分がEDI以外であれば書き換え
      if (chosenManifestType === 'dxe_form' && emissionDetail && emissionDetail?.manifest_type !== 'dxe_form') {
        this.manifest.manifest_type = emissionDetail?.manifest_type;
        this.showChangedManifestTypeMessage(chosenManifestType);
      }

      this.searchDialogCancelCallback();
    },

    /**
     * 複写ロード時のチェック処理
     */
    initValidateManifestTypeState(initialLoad = false) {
      const manifestType = this.manifest.manifest_type;
      const emissionManifestType = this.manifest.emission_details.manifest_type;
      let hasNonSubscribeTrader = false;

      this.manifestTypeMismatchMessage =
        "現在の排出事業者のマスタ区分と発行するマニフェストの区分が異なります。<br />" +
        "発行するマニフェストの区分を排出事業者のマスタ区分に合わせたい場合は、手動でマニフェスト区分を選択してください。";

      if (initialLoad) {
        hasNonSubscribeTrader =
          this.manifest.effective_transport_route_include_non_subscribe_trader;
      } else {
        const transport_route = this.transportRoutes.find(
          (route) => route.id === this.manifest.transport_route_id
        );
        if (transport_route && transport_route?.include_non_subscribe_trader) {
          hasNonSubscribeTrader = true;
        }
      }

      if ( emissionManifestType === "dxe_form" && !hasNonSubscribeTrader && manifestType !== "dxe_form" ) {
        /**
         * ・排出事業者がEDIで、ルートがEDIだが、マニフェスト区分がWeb・紙の場合はポップアップ表示する（10秒）。
         */
        this.manifestTypeMismatchAlert = true;

      } else if ( emissionManifestType === "other_form" && !hasNonSubscribeTrader && ["paper_form"].includes(manifestType) ) {
        /**
         * ・排出事業者がWebで、ルートが未加入業者を含まないルートの場合、マニフェスト区分が紙の場合は、ポップアップ表示する（10秒）。
         */
        this.manifestTypeMismatchAlert = true;

      } else if ( emissionManifestType === "paper_form" && manifestType === "other_form" ) {
        /**
         * ・排出事業者が紙だが、マニフェスト区分がWebの場合は、ポップアップ表示する（10秒）。
         */
        this.manifestTypeMismatchAlert = true;
      }
      if (this.manifestTypeMismatchAlert) {
        setTimeout(() => {
          this.manifestTypeMismatchAlert = false;
        }, 20000);
      }
      return !this.manifestTypeMismatchAlert;
    },

    /**
     * 保存時のチェック処理
     * @return { boolean }
     */
    beforeSaveValidateManifestTypeState() {
      this.manifestTypeMismatchAlert = false;

      const manifestType = this.manifest?.manifes_type;
      const emissionManifestType = this.manifest?.emission_details?.manifest_type;

      this.manifestTypeMismatchMessage =
        "利用出来ないマニフェスト区分が選択されています<br />（マニフェスト区分を手動で選択し直してください）";

      /**
       * 保存時エラー対応
       * ・排出事業者がWebだが、マニフェスト区分がEDIの場合は、保存時エラー。
       * ・排出事業者が紙だが、マニフェスト区分がEDIの場合は、保存時エラー。
       */
      if ( ["other_form", "paper_form"].includes(emissionManifestType) && manifestType === "dxe_form" ) {
        this.manifestTypeMismatchAlert = true;
      }
      if (this.manifestTypeMismatchAlert) {
        setTimeout(() => {
          this.manifestTypeMismatchAlert = false;
        }, 20000);
      }
      return !this.manifestTypeMismatchAlert;
    },

    /**
     * JWNET取扱品目 チェンジイベント
     */
    itemChangeEvent() {
      if (this.manifest.item_id) {
        let selected = this.items.find(
          (v) => v.value === this.manifest.item_id
        );
        if (selected) {
          this.manifest.item_name = selected.text;
        }
      }
      this.setTransportRoutes();
    },

    /**
     * 運搬担当者 チェンジイベント
     * @param item { String }
     */
    transportUserChangeEvent(item) {
      const transportUser = this.drivers.find(
        (v) => v.text.replace(/\s+/g, "") === item.replace(/\s+/g, "")
      );
      if (transportUser) {
        this.manifest.transport_user_id = transportUser.value;
      } else {
        this.manifest.transport_user_id = null;
      }
    },

    /**
     * 運搬担当者 クリア クリックイベント
     */
    transportUserClearClickEvent() {
      this.manifest.transport_user_id = null;
      this.manifest.transport_user_name = null;
    },

    /**
     * スポット排出事業場チェックボックス チェンジイベント
     */
    spotEmissionLocationChangeEvent() {
      let emissionLocationId = null;
      if (this.manifest.use_spot_emission_location) {
        this.manifest.supplierable_location_id = null;
        this.manifest.supplierable_location_code = null;
        this.selectedEmission.location = null;
      } else {
        this.selectedEmission.location = {
          id: this.backupSelectedEmission.location.id,
          code: this.backupSelectedEmission.location.code,
          name: this.backupSelectedEmission.location.name,
          zip_code: this.backupSelectedEmission.location.zip_code,
          prefecture_code: this.backupSelectedEmission.location.prefecture_code,
          address: this.backupSelectedEmission.location.address,
          phone_number: this.backupSelectedEmission.location.phone_number,
        };
        this.manifest.supplierable_location_id = this.backupSelectedEmission.location.id;
        this.manifest.supplierable_location_code = this.backupSelectedEmission.location.code;
        emissionLocationId = this.backupSelectedEmission.location.id;
      }
      this.searchEmissionRegistersBy(
        this.manifest.supplierable_id,
        emissionLocationId
      );
      this.searchEmissionDeliverersBy(
        this.manifest.supplierable_id,
        emissionLocationId
      );
    },

    /**
     * スポット排出事業場編集ボタン クリックイベント
     */
    editSpotLocationButtonClickEvent() {
      this.spotLocation = null;
      if (this.manifest.lock_spot_emission_location) {
        this.spotLocation = {
          name: this.manifest.emission_location_name,
          zip_code: this.manifest.emission_location_zip_code,
          prefecture_code: this.manifest.emission_location_prefecture_code,
          address_1: this.manifest.emission_location_address_1,
          address_2: this.manifest.emission_location_address_2,
          address_3: this.manifest.emission_location_address_3,
          phone_number: this.manifest.emission_location_phone_number,
        };
      }
      this.enableEditSpotLocation = true;
    },

    /**
     * スポット排出事業場モーダル コールバック
     */
    spotLocationCancelCallback() {
      this.enableEditSpotLocation = false;
    },

    /**
     * フリー登録 排出事業者
     * 「住所→郵便番号」ボタン クリックイベント
     */
    searchFreeEmissionZipButtonClickEvent() {
      this.enableSearchFreeEmissionZipModal = true;
    },

    /**
     * フリー登録 排出事業者
     * 「住所→郵便番号」ボタン コールバック
     * @param address { object } 住所情報
     * @param address { object } 住所情報
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { string } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    reflectFreeEmissionFullCallback(address) {
      this.manifest.emission_zip_code = address.zip_code;
      this.manifest.emission_prefecture_code = `${address.prefecture_code}`;
      this.manifest.emission_address_1 = address.city ? address.city : "";
      this.manifest.emission_address_2 = address.town ? address.town : "";
    },

    /**
     * フリー登録 排出事業者
     * 「住所→郵便番号」ボタン コールバック
     * @param zipCode { string } 郵便番号
     */
    reflectFreeEmissionZipCallback(zipCode) {
      this.manifest.emission_zip_code = zipCode;
    },

    /**
     * フリー登録 排出事業場
     * 「住所→郵便番号」ボタン クリックイベント
     */
    searchFreeEmissionLocationZipButtonClickEvent() {
      this.enableSearchFreeEmissionLocationZipModal = true;
    },

    /**
     * フリー登録 排出事業場
     * 「住所→郵便番号」ボタン コールバック
     * @param address { object } 住所情報
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { string } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    reflectFreeEmissionLocationFullCallback(address) {
      this.manifest.emission_location_zip_code = address.zip_code;
      this.manifest.emission_location_prefecture_code = `${address.prefecture_code}`;
      this.manifest.emission_location_address_1 = address.city
        ? address.city
        : "";
      this.manifest.emission_location_address_2 = address.town
        ? address.town
        : "";
    },

    /**
     * フリー登録 排出事業場
     * 「住所→郵便番号」ボタン コールバック
     * @param zipCode { string } 郵便番号
     */
    reflectFreeEmissionLocationZipCallback(zipCode) {
      this.manifest.emission_location_zip_code = zipCode;
    },

    /**
     * スポット排出事業場モーダル コールバック
     */
    spotLocationRegisterCallback(location) {
      this.manifest.lock_spot_emission_location = true;
      this.manifest.emission_location_id = null;
      this.manifest.emission_location_code = null;
      this.selectedEmission.location = {
        name: location.name,
        zip_code: location.zip_code,
        address: location.address,
        phone_number: location.phone_number,
      };

      this.manifest.emission_location_name = location.name;
      this.manifest.emission_location_zip_code = location.zip_code;
      this.manifest.emission_location_prefecture_code = location.prefecture_code;
      this.manifest.emission_location_address_1 = location.address_1;
      this.manifest.emission_location_address_2 = location.address_2;
      this.manifest.emission_location_address_3 = location.address_3;
      this.manifest.emission_location_phone_number = location.phone_number;
      this.spotLocationCancelCallback();
    },

    rebuildManifestTypes(manifestTypes, invalidTypes) {
      manifestTypes.forEach((manifestType) => {
        if (invalidTypes.includes(manifestType.value)) {
          manifestType.valid = false;
        }
      })
      return manifestTypes;
    },

    /**
     * TODO
     * マニフェスト区分の入れ替えを行う.（ラジオボタンの表示内容の展開）
     * modifyManifestTypesが参照されるたびに実行 以下のデータが変更されるたびに実行される
     *
     * this.manifestTypes - マニフェスト区分のマスタデータ
     * this.manifest.form_type - フォームタイプ
     * this.selectedEmission - 選択された排出事業者
     * this.manifest.transport_route_id - 運搬ルートID
     * this.transportRoutes - 運搬ルート一覧
     */
    buildManifestTypeMembers() {
      if (!this.manifestTypes) {
        return [];
      }

      // 運搬ルート取得
      const transportRoute = this.transportRoutes.find(
        (v) => v.id === this.manifest.transport_route_id
      );

      const workManifestTypes = structuredClone(this.manifestTypes);

      // フリー登録の場合、EDIを非活性
      if (this.manifest.form_type === "free_mode") {
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);
      }

      if (transportRoute && transportRoute.include_non_subscribe_trader) {
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form', 'other_form']);
      }

      // 排出事業者が未選択の場合、フル活性
      if (!this.selectedEmission) {
        return workManifestTypes;
      }

      // EDIでない、またはEDI起票不可の場合、Web・紙のみ
      const isEdiAvailable =　this.selectedEmission.manifest_type === "dxe_form" &&　!this.selectedEmission.not_possible_manifest;

      if (!isEdiAvailable) {
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);
      }

      // 2次マニフェストの場合、EDI・Web・紙すべて表示
      // if (this.level !== "first_manifest") {
      //   console.log("2次マニフェストの場合、EDI・Web・紙すべて表示")
      //   return workManifestTypes;
      // }

      // 1次マニフェストでEDI利用可能な場合

      // 運搬ルート未設定の場合、EDI・Web・紙すべて表示
      if (!this.manifest.transport_route_id) {
        return workManifestTypes;
      }

      if (!transportRoute) {
        return workManifestTypes;
      }

      /**
       * JWNET未加入業者を含む
       * 排出自己運搬で、自社を含まない
       * 1次マニで他社運搬
       */
      if (transportRoute.include_non_subscribe_trader) {
        // 未加入業者を含む
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form', 'other_form']);

      } else if (transportRoute.is_self_emission && !transportRoute.self_emission_include_own) {
        // 排出自己運搬＋区間１運搬先が他社のルート
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);

      } else if (this.level === "first_manifest" && transportRoute.other_transport) {
        // 区間１収運業者が他社のルート
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);

      }

      // EDIが使えるルートかチェック
      /**
       * 1区間目の運搬業者が自社
       * 排出自己運搬で、自社含む
       * 委託他社
       * @type {boolean|*}
       */
      const canUseEdi =
        transportRoute.first_trader_owned ||
        (transportRoute.is_self_emission && transportRoute.self_emission_include_own) ||
        transportRoute.consign_other_transport;
      return canUseEdi ? workManifestTypes : this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);
    },

    /**
     * マニフェスト区分の制御（ラジオボタンの選択状態の制御）
     */
    correctionManifestType() {

      if (this.manifest.form_type === "master_mode") {
        // マスターからの登録
        // 排出事業者未選択の場合
        if (!this.selectedEmission) {
          return;
        }

        /**
         * 選択された収集運搬業者のマニフェスト区分がEDI以外で、現在選択中のマニフェスト区分がEDIの場合
         */
        if (this.selectedEmission.manifest_type !== "dxe_form" && this.manifest.manifest_type === 'dxe_form') {
          const firstType = this.modifyManifestTypes.find(v => v.valid)
          if (firstType) {
            this.manifest.manifest_type = firstType.value;
          }
        }

      } else {
        // フリー登録の場合、EDIは使用不可
        if (this.manifest.manifest_type === "dxe_form") {
          this.manifest.manifest_type = "other_form";
        }
      }
    },

    /**
     * 「マスターから・フリー登録」の切り替え クリックイベント
     * @param { string } form_type master_mode: マスターから登録, free_mode: フリー登録
     */
    formTypeButtonClickEvent(form_type) {
      const chosenManifestType = this.manifest.manifest_type;
      this.manifest.form_type = form_type;
      if (!this.manifest.id) {
        this.correctionManifestType();
      }
      this.showChangedManifestTypeMessage(chosenManifestType);
    },

    /**
     * 複写して作成時の初期処理(現時点ではマスタのマニ区分変更対応のみ)
     */
    duplicateSetting() {
      if (this.manifest.form_type === "master_mode") {
        // マスターからの登録
        if (this.selectedEmission) {
          // 複写元マニがEDIだけどマスタに変更があってEDIが使えなくなった場合
          if (
            this.manifest.manifest_type === "dxe_form" &&
            this.selectedEmission.manifest_type !== "dxe_form"
          ) {
            this.manifest.manifest_type = "other_form";
          }
        }
      }
    },

    /**
     * 運搬ルート行選択
     * @param item
     * @property item.other_transport { Boolean }
     * @property item.other_trader { Boolean }
     * @param row
     * @return {string}
     */
    transportRowClick(item, row) {
      if (!this.manifest.can_change_transport_route) {
        return false;
      }

      // 選択中のマニフェスト区分
      const chosenManifestType = this.manifest.manifest_type;
      this.manifest.transport_route_id = item.id;
      this.manifest.disposal_method_id = item.disposal_method_id;
      this.manifest.disposer_subscriber_number = item.disposer_subscriber_number;

      row.select(true);

      if (item.include_non_subscribe_trader) {
        // 未加入業者を含む
        this.manifest.manifest_type = "paper_form";
      } else if (this.manifest.manifest_type === 'dxe_form' && item.is_self_emission && !item.self_emission_include_own) {
        // 排出自己運搬＋区間１運搬先が他社のルート
        this.manifest.manifest_type = "other_form";
      } else if (this.level === "first_manifest" && this.manifest.manifest_type === 'dxe_form' && item.other_transport) {
        // 区間１収運業者が他社のルート
        this.manifest.manifest_type = "other_form";
      }

      this.showChangedManifestTypeMessage(chosenManifestType);
    },

    /**
     * @param oldSelectedManifestType
     */
    showChangedManifestTypeMessage(oldSelectedManifestType) {
      if (oldSelectedManifestType !== this.manifest.manifest_type) {
        const manifestType = this.manifestTypes.find(
          (v) => v.value === this.manifest.manifest_type
        )?.text;
        this.showInfo({
          message: `マニフェスト区分が ${manifestType} に変更されました`,
        });
      }
    },

    /**
     * 運搬ルート背景色設定
     * @param item
     * @return {string}
     */
    transportRouteRowBackground(item) {
      return item.id === this.manifest.transport_route_id ? "v-data-table__selected" : "";
    },

    /**
     * 運搬ルートの取得
     */
    setTransportRoutes() {
      // すべてのモードで共通する検索条件。区間1の収運業者が自社のルートは除外する。
      const conditions = {
        without_invisible: true,
        item_type: 'Item',
        item_id: this.manifest.item_id,
        first_trader_owned: false,
      };

      // 2次マニフェストでは、報告不要の処分ルートを除外する。
      if (this.level !== "first_manifest") {
        // conditions['without_self_emission'] = true;
        conditions['without_non_reporting'] = true;
      }

      // 処分単体モードでは、持込と他社運搬のルートだけを検索する。
      if (this.level === "first_manifest" && this.mode === "disposal") {
        conditions.self_emission = true;
        conditions.proxy_request = true;
      }

      this.fetchTransportRoutes({ ...conditions }).then((content) => {
        this.transportRoutes = content.transport_routes;

        // 候補が1件だけなら、ルート選択と同時に関連項目も設定する。
        if (this.transportRoutes.length === 1) {
          const route = this.transportRoutes[0];
          this.manifest.transport_route_id = route.id;
          this.manifest.disposal_method_id = route.disposal_method_id;
          this.manifest.disposer_subscriber_number = route.disposer_subscriber_number;
        }
      });

    },

    /**
     * 引渡担当者の取得
     * @param emission_id { number } 排出事業者のID
     * @param emission_location_id { number } 排出事業場のID
     */
    searchEmissionDeliverersBy(emission_id, emission_location_id) {
      this.fetchDeliverers({
        id: emission_id,
        emission_location_id: emission_location_id,
      }).then((content) => {
        this.emissionDeliverers = content.deliverers;
      });
    },

    /**
     * 2次マニで利用する引渡担当者、登録担当者のリストを展開する.
     */
    searchSecondManifestUsers() {
      if (this.$route.params.level === "second_manifest") {
        this.fetchUsers({without_invisible: true}).then((content) => {
          this.emissionDeliverers = content.users;
          this.emissionRegisters = content.users;
        });
      }
    },

    /***
     * 登録担当者の取得
     * @param emission_id { number } 排出事業者のID
     * @param emission_location_id { number } 排出事業場のID
     */
    searchEmissionRegistersBy(emission_id, emission_location_id) {
      this.fetchRegisters({
        id: emission_id,
        emission_location_id: emission_location_id,
      }).then((content) => {
        this.emissionRegisters = content.registers;
      });
    },

    /**
     * モーダルキャンセル コールバック
     */
    searchDialogCancelCallback() {
      this.enableSearchEmissionDialog = false;
      this.enableSearchTenantDisposerDialog = false;
    },

    searchEmissionButtonClickEvent() {
      if (this.level === "first_manifest") {
        this.enableSearchEmissionDialog = true;
      } else {
        this.enableSearchTenantDisposerDialog = true;
      }
    },
    /**
     *
     */
    removeHazardousSubstances() {
      this.manifest.hazardous_substance_1_id = null;
      this.manifest.hazardous_substance_2_id = null;
      this.manifest.hazardous_substance_3_id = null;
      this.manifest.hazardous_substance_4_id = null;
      this.manifest.hazardous_substance_5_id = null;
      this.manifest.hazardous_substance_6_id = null;
    },
    setEmissionZipAddress(content) {
      this.manifest.emission_prefecture_code = `${content.pref_code}`;
      this.manifest.emission_address_1 = `${content.locality}`;
      this.manifest.emission_address_2 = `${content.street}`;
    },
    setEmissionLocationZipAddress(content) {
      this.manifest.emission_location_prefecture_code = `${content.pref_code}`;
      this.manifest.emission_location_address_1 = `${content.locality}`;
      this.manifest.emission_location_address_2 = `${content.street}`;
    },

    /**
     * フリーモード
     * 排出事業者情報を事業場にコピーするボタンクリックイベント
     */
    copyEmissionToEmissionLocationButtonClickEvent() {
      this.manifest.emission_location_name = this.manifest.emission_name;
      this.manifest.emission_location_zip_code =
        this.manifest.emission_zip_code;
      this.manifest.emission_location_prefecture_code =
        this.manifest.emission_prefecture_code;
      this.manifest.emission_location_address_1 =
        this.manifest.emission_address_1;
      this.manifest.emission_location_address_2 =
        this.manifest.emission_address_2;
      this.manifest.emission_location_address_3 =
        this.manifest.emission_address_3;
      this.manifest.emission_location_phone_number =
        this.manifest.emission_phone_number;
    },

    /**
     * 閉じるボタン クリックイベント
     */
    cancelButtonClickEvent() {
      this.$router.push({name: "DriverManifestManagements"});
    },

    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent() {
      const $self = this;

      if (!this.beforeSaveValidateManifestTypeState()) {
        this.reloadKey = this.reloadKey + 1;
        return false;
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.managementManifestValidator.validateWithInfo().then(({isValid, errors}) => {
            if (isValid) {
              $self.sendRegistrationInformation();
            } else {
              let messages = [];
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0]);
                }
              }
              let unique_message = [...new Set(messages)];
              this.showError({message: unique_message.join(",")});
            }
          });
          resolve();
        }, 1000);
      });
    },

    /**
     * 本登録ボタン クリックイベント
     */
    receptionRegisterButtonClickEvent() {
      const $self = this;

      if (!this.beforeSaveValidateManifestTypeState()) {
        this.reloadKey = this.reloadKey + 1;
        return false;
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.managementManifestValidator.validateWithInfo().then(({isValid, errors}) => {
            if (isValid) {
              $self.sendReceptionRegistrationInformation();
            } else {
              let messages = [];
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0]);
                }
              }
              let unique_message = [...new Set(messages)];
              this.showError({message: unique_message.join(",")});
            }
          });
          resolve();
        }, 1000);
      });
    },

    /**
     * 予約登録ボタン クリックイベント
     */
    reservationRegisterButtonClickEvent() {
      const $self = this;

      if (!this.beforeSaveValidateManifestTypeState()) {
        this.reloadKey = this.reloadKey + 1;
        return false;
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.managementManifestValidator.validateWithInfo().then(({isValid, errors}) => {
            if (isValid) {
              $self.sendReservationRegistrationInformation();
            } else {
              let messages = [];
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0]);
                }
              }
              let unique_message = [...new Set(messages)];
              this.showError({message: unique_message.join(",")});
            }
          });
          resolve();
        }, 1000);
      });
    },

    /**
     * 登録ボタン
     */
    sendRegistrationInformation() {
      let params = this.buildForm();
      if (this.$route.params.id) {
        this.updateManifest({id: this.manifest.id, params: params}).then(() => {
          this.$router.push({name: "DriverManifestManagements"});
        });
      } else {
        this.createManifest({params: params}).then(() => {
          this.$router.push({name: "DriverManifestManagements"});
        });
      }
    },

    /**
     * 本登録ボタン
     */
    sendReceptionRegistrationInformation() {
      const params = this.buildForm();
      if (this.$route.params.id) {
        this.updateManifestRegistration({id: this.manifest.id, params: params}).then(() => {
          this.$router.push({name: "DriverManifestManagements"});
        });
      } else {
        this.createManifestRegistration({params: params}).then(() => {
          this.$router.push({name: "DriverManifestManagements"});
        });
      }
    },

    /**
     * 予約登録ボタン
     */
    sendReservationRegistrationInformation() {
      const params = this.buildForm();
      if (this.$route.params.id) {
        this.updateManifestReservation({id: this.manifest.id, params: params}).then(() => {
          this.$router.push({name: "DriverManifestManagements"});
        });
      } else {
        this.createManifestReservation({params: params}).then(() => {
          this.$router.push({name: "DriverManifestManagements"});
        });
      }
    },

    /**
     * 引渡日 コールバック
     * @param value
     */
    collectedDateChangedCallback(value) {
      this.manifest.collected_date = value;
    },

    /**
     * A票返却日変更　コールバック
     */
    paperManifestReturnDateAChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.a_return_date = value;
    },

    /**
     * B2票返却日変更　コールバック
     */
    paperManifestReturnDateB2ChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.b2_return_date = value;
    },

    /**
     * B4票返却日変更　コールバック
     */
    paperManifestReturnDateB4ChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.b4_return_date = value;
    },

    /**
     * B6票返却日変更　コールバック
     */
    paperManifestReturnDateB6ChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.b6_return_date = value;
    },

    /**
     * D票返却日変更　コールバック
     */
    paperManifestReturnDateDChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.d_return_date = value;
    },

    /**
     * E票返却日変更　コールバック
     */
    paperManifestReturnDateEChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.e_return_date = value;
    },

    /**
     * B1票返却日変更　コールバック
     */
    paperManifestReturnDateB1ChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.b1_return_date = value;
    },

    /**
     * C1票返却日変更　コールバック
     */
    paperManifestReturnDateC1ChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.c1_return_date = value;
    },

    /**
     * C2票返却日変更　コールバック
     */
    paperManifestReturnDateC2ChangedCallback(value) {
      this.manifest.paper_manifest_return_vote.c2_return_date = value;
    },

    /**
     * 送信情報の作成
     * @return {FormData}
     */
    buildForm() {
      const params = new FormData();
      params.append("form_type", this.nullToBlank(this.manifest.form_type));
      params.append(
        "dxe_vote_staff",
        this.nullToBlank(this.manifest.dxe_vote_staff)
      );
      if (this.manifest.form_type === "master_mode") {
        params.append(
          "supplierable_id",
          this.nullToBlank(this.manifest.supplierable_id)
        );
        params.append(
          "supplierable_type",
          this.nullToBlank(this.manifest.supplierable_type)
        );
        params.append(
          "supplierable_location_code",
          this.nullToBlank(this.manifest.supplierable_location_code)
        );

        params.append(
          "use_spot_emission_location",
          this.nullToBlank(this.manifest.use_spot_emission_location)
        );

        if (
          this.manifest.use_spot_emission_location &&
          this.manifest.lock_spot_emission_location
        ) {
          params.append(`supplierable_location_code`, this.nullToBlank(null));
          params.append(
            `emission_location_name`,
            this.nullToBlank(this.manifest.emission_location_name)
          );
          params.append(
            `emission_location_zip_code`,
            this.nullToBlank(this.manifest.emission_location_zip_code)
          );
          params.append(
            `emission_location_prefecture_code`,
            this.nullToBlank(this.manifest.emission_location_prefecture_code)
          );
          params.append(
            `emission_location_address_1`,
            this.nullToBlank(this.manifest.emission_location_address_1)
          );
          params.append(
            `emission_location_address_2`,
            this.nullToBlank(this.manifest.emission_location_address_2)
          );
          params.append(
            `emission_location_address_3`,
            this.nullToBlank(this.manifest.emission_location_address_3)
          );
          params.append(
            `emission_location_phone_number`,
            this.nullToBlank(this.manifest.emission_location_phone_number)
          );
        }
      } else {
        params.append(
          "emission_name",
          this.nullToBlank(this.manifest.emission_name)
        );
        params.append(
          "emission_zip_code",
          this.nullToBlank(this.manifest.emission_zip_code)
        );
        params.append(
          "emission_prefecture_code",
          this.nullToBlank(this.manifest.emission_prefecture_code)
        );
        params.append(
          "emission_address_1",
          this.nullToBlank(this.manifest.emission_address_1)
        );
        params.append(
          "emission_address_2",
          this.nullToBlank(this.manifest.emission_address_2)
        );
        params.append(
          "emission_address_3",
          this.nullToBlank(this.manifest.emission_address_3)
        );
        params.append(
          "emission_phone_number",
          this.nullToBlank(this.manifest.emission_phone_number)
        );

        params.append(
          "emission_location_name",
          this.nullToBlank(this.manifest.emission_location_name)
        );
        params.append(
          "emission_location_zip_code",
          this.nullToBlank(this.manifest.emission_location_zip_code)
        );
        params.append(
          "emission_location_prefecture_code",
          this.nullToBlank(this.manifest.emission_location_prefecture_code)
        );
        params.append(
          "emission_location_address_1",
          this.nullToBlank(this.manifest.emission_location_address_1)
        );
        params.append(
          "emission_location_address_2",
          this.nullToBlank(this.manifest.emission_location_address_2)
        );
        params.append(
          "emission_location_address_3",
          this.nullToBlank(this.manifest.emission_location_address_3)
        );
        params.append(
          "emission_location_phone_number",
          this.nullToBlank(this.manifest.emission_location_phone_number)
        );
      }

      params.append(
        "contact_number_1",
        this.nullToBlank(this.manifest.contact_number_1)
      );
      params.append(
        "contact_number_2",
        this.nullToBlank(this.manifest.contact_number_2)
      );
      params.append(
        "contact_number_3",
        this.nullToBlank(this.manifest.contact_number_3)
      );
      params.append(
        "deliver_staff",
        this.nullToBlank(this.manifest.deliver_staff)
      );
      params.append(
        "register_staff",
        this.nullToBlank(this.manifest.register_staff)
      );
      params.append("item_type", "Item");
      params.append("item_id", this.nullToBlank(this.manifest.item_id));
      params.append("waste_name", this.nullToBlank(this.manifest.waste_name));
      if (this.manifest.manifest_type !== "dxe_form") {
        params.append(
          "manifest_no",
          this.nullToBlank(this.manifest.manifest_no)
        );
      }
      params.append("packing_id", this.nullToBlank(this.manifest.packing_id));
      params.append(
        "packing_quantity",
        this.nullToBlank(this.manifest.packing_quantity)
      );
      params.append("quantity", this.nullToBlank(this.manifest.quantity));
      params.append(
        "quantity_unit_id",
        this.nullToBlank(this.manifest.quantity_unit_id)
      );
      params.append(
        "quantity_confirm_id",
        this.nullToBlank(this.manifest.quantity_confirm_id)
      );
      params.append(
        "hazardous_substance_1_id",
        this.nullToBlank(this.manifest.hazardous_substance_1_id)
      );
      params.append(
        "hazardous_substance_2_id",
        this.nullToBlank(this.manifest.hazardous_substance_2_id)
      );
      params.append(
        "hazardous_substance_3_id",
        this.nullToBlank(this.manifest.hazardous_substance_3_id)
      );
      params.append(
        "hazardous_substance_4_id",
        this.nullToBlank(this.manifest.hazardous_substance_4_id)
      );
      params.append(
        "hazardous_substance_5_id",
        this.nullToBlank(this.manifest.hazardous_substance_5_id)
      );
      params.append(
        "hazardous_substance_6_id",
        this.nullToBlank(this.manifest.hazardous_substance_6_id)
      );
      params.append(
        "disposal_method_id",
        this.nullToBlank(this.manifest.disposal_method_id)
      );
      params.append(
        "transportation_id",
        this.nullToBlank(this.manifest.transportation_id)
      );
      params.append(
        "transport_route_id",
        this.nullToBlank(this.manifest.transport_route_id)
      );
      params.append(
        "disposer_subscriber_number",
        this.nullToBlank(this.manifest.disposer_subscriber_number)
      );
      params.append(
        "collected_date",
        this.nullToBlank(this.manifest.collected_date)
      );
      params.append("note_1", this.nullToBlank(this.manifest.note_1));
      params.append("note_2", this.nullToBlank(this.manifest.note_2));
      params.append("note_3", this.nullToBlank(this.manifest.note_3));
      params.append("note_4", this.nullToBlank(this.manifest.note_4));
      params.append("note_5", this.nullToBlank(this.manifest.note_5));
      params.append(
        "manifest_type",
        this.nullToBlank(this.manifest.manifest_type)
      );
      params.append(
        "transport_user_id",
        this.nullToBlank(this.manifest.transport_user_id)
      );
      params.append(
        "transport_user_name",
        this.nullToBlank(this.manifest.transport_user_name)
      );
      params.append(
        "vehicle_number",
        this.nullToBlank(this.manifest.vehicle_number)
      );
      params.append(
        "manifest_search_keyword",
        this.nullToBlank(this.manifest.manifest_search_keyword)
      );
      params.append("level", this.nullToBlank(this.manifest.level));
      if (this.manifest.manifest_type === "paper_form") {
        params.append(
          "paper_manifest_return_votes_attributes[]id",
          this.nullToBlank(this.manifest.paper_manifest_return_vote.id)
        );
        params.append(
          "paper_manifest_return_votes_attributes[]tenant_id",
          this.nullToBlank(this.manifest.paper_manifest_return_vote.tenant_id)
        );
        params.append(
          "paper_manifest_return_votes_attributes[]a_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.a_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]a_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.a_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b2_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b2_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b2_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b2_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b4_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b4_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b4_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b4_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b6_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b6_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b6_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b6_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]d_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.d_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]d_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.d_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]e_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.e_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]e_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.e_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b1_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b1_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]b1_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.b1_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]c1_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.c1_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]c1_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.c1_return_target
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]c2_return_date",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.c2_return_date
          )
        );
        params.append(
          "paper_manifest_return_votes_attributes[]c2_return_target",
          this.nullToBlank(
            this.manifest.paper_manifest_return_vote.c2_return_target
          )
        );
      }
      params.append(
        "manifest_custom_label_attributes[id]",
        this.nullToBlank(this.manifest.manifest_custom_label.id)
      );
      params.append(
        "manifest_custom_label_attributes[contractor_id]",
        this.nullToBlank(this.manifest.manifest_custom_label.contractor_id)
      );
      params.append(
        "manifest_custom_label_attributes[custom_labels_1]",
        this.nullToBlank(this.manifest.manifest_custom_label.custom_labels_1)
      );
      params.append(
        "manifest_custom_label_attributes[custom_labels_2]",
        this.nullToBlank(this.manifest.manifest_custom_label.custom_labels_2)
      );
      params.append(
        "manifest_custom_label_attributes[custom_labels_3]",
        this.nullToBlank(this.manifest.manifest_custom_label.custom_labels_3)
      );
      params.append(
        "manifest_custom_label_attributes[custom_labels_4]",
        this.nullToBlank(this.manifest.manifest_custom_label.custom_labels_4)
      );
      params.append(
        "manifest_custom_label_attributes[custom_labels_5]",
        this.nullToBlank(this.manifest.manifest_custom_label.custom_labels_5)
      );
      params.append(
        "manifest_custom_label_attributes[custom_labels_6]",
        this.nullToBlank(this.manifest.manifest_custom_label.custom_labels_6)
      );

      return params;
    },

    /**
     * 連絡番号 追加ボタン クリックイベント
     */
    addContactNumberClick() {
      if (this.displayContactNumber2) {
        this.displayContactNumber3 = true;
      } else {
        this.displayContactNumber2 = true;
      }
    },

    /**
     * 備考 追加ボタン クリックイベント
     */
    addNoteClick() {
      if (this.displayNote4) {
        this.displayNote5 = true;
      } else if (this.displayNote3) {
        this.displayNote4 = true;
      } else if (this.displayNote2) {
        this.displayNote3 = true;
      } else {
        this.displayNote2 = true;
      }
    },
    controlNotes() {
      if (this.manifest.note_5) {
        this.displayNote2 = true;
        this.displayNote3 = true;
        this.displayNote4 = true;
        this.displayNote5 = true;
      } else if (this.manifest.note_4) {
        this.displayNote2 = true;
        this.displayNote3 = true;
        this.displayNote4 = true;
      } else if (this.manifest.note_3) {
        this.displayNote2 = true;
        this.displayNote3 = true;
      } else if (this.manifest.note_2) {
        this.displayNote2 = true;
      }
    },
    controlContactNumbers() {
      if (this.manifest.contact_number_3) {
        this.displayContactNumber2 = true;
        this.displayContactNumber3 = true;
      } else if (this.manifest.contact_number_2) {
        this.displayContactNumber2 = true;
      }
    },

    /**
     * モーダル コールバック
     */
    closeModalCallback() {
      this.enableSearchFreeEmissionZipModal = false;
      this.enableEditSpotLocation = false;
      this.enableSearchFreeEmissionLocationZipModal = false;
    },

    reflectHistoryCallback(item) {
      this.stopLocationAutoSelect = true;

      /**
       * 複写から除外する項目
       * ・collected_date: 回収日
       * ・manifest_no: マニフェスト番号
       * ・emission_details: 排出事業者情報
       * @type {string[]}
       */
      const excludeKeys = ['collected_date', 'manifest_no', 'emission_details']
      for (const key in item) {
        if (!excludeKeys.includes(key)) {
          this.manifest[key] = item[key]
        }
      }

      this.selectedEmission = item.emission_details;

      this.selectableSupplierIds = [item.supplierable_id, item.supplierable_location_id]
      this.showInfo({message: "直近の回収履歴を反映しました"});
    },
    formattedCollectedDate() {
      if (!this.manifest.collected_date) {
        return 'ー'
      }
      return this.$dayjs(this.manifest.collected_date).format("YYYY/MM/DD");
    },
    manifestTypeLabel () {
      const manifestType = this.modifyManifestTypes.find(v => v.value === this.manifest.manifest_type);
      return manifestType?.text;
    },
  },

};
</script>

<style scoped lang="scss">
.text-small {
  font-size: .805rem !important;
}

.radio-selected {
  background-color: #dfeffd !important;
}

</style>
